<template>
  <div class="ai-chatbox-wrapper">
    <!-- Chat Window -->
    <Transition name="chatbox">
      <div v-if="isOpen" class="ai-chatbox" :class="{ 'expanded': isExpanded }">
        <!-- Header -->
        <div class="chatbox-header">
          <div class="header-left">
            <div class="header-icon" :class="{ 'thinking': loading }">
              <vue-feather type="cpu" size="18" />
            </div>
            <span class="header-title">TestPal AI</span>
            <span :class="['status-badge', aiStatus, { 'pulse': loading }]">
              {{ loading ? 'thinking' : aiStatus }}
            </span>
          </div>
          <div class="header-actions">
            <button @click="toggleMode" class="header-btn" :title="activeMode === 'chat' ? 'Create Mode' : 'Chat Mode'">
              <vue-feather :type="activeMode === 'chat' ? 'plus-circle' : 'message-circle'" size="16" />
            </button>
            <button @click="toggleExpanded" class="header-btn" :title="isExpanded ? 'Minimize' : 'Expand'">
              <vue-feather :type="isExpanded ? 'chevron-down' : 'chevron-up'" size="16" />
            </button>
          </div>
        </div>

        <!-- Mode Tabs -->
        <div class="mode-tabs">
          <button @click="activeMode = 'chat'" :class="['mode-tab', { active: activeMode === 'chat' }]">
            <vue-feather type="message-circle" size="14" />
            <span>Chat</span>
          </button>
          <button @click="activeMode = 'create'" :class="['mode-tab', { active: activeMode === 'create' }]">
            <vue-feather type="plus-circle" size="14" />
            <span>Create</span>
          </button>
          <button @click="activeMode = 'generate'" :class="['mode-tab', { active: activeMode === 'generate' }]">
            <vue-feather type="zap" size="14" />
            <span>Generate</span>
          </button>
        </div>

        <!-- CREATE MODE: Direct Content Creation -->
        <div v-if="activeMode === 'create'" class="create-mode">
          <div class="create-header">
            <h4>Quick Create</h4>
            <p>Create content directly with AI assistance</p>
          </div>

          <div class="create-cards">
            <div v-for="card in createCards" :key="card.type" 
              class="create-card" @click="openCreateModal(card)">
              <div class="card-icon" :style="{ background: card.gradient }">
                <vue-feather :type="card.icon" size="20" />
              </div>
              <div class="card-info">
                <span class="card-title">{{ card.title }}</span>
                <span class="card-desc">{{ card.description }}</span>
              </div>
              <vue-feather type="chevron-right" size="16" class="card-arrow" />
            </div>
          </div>
        </div>

        <!-- GENERATE MODE: AI Content Generation -->
        <div v-if="activeMode === 'generate'" class="generate-mode">
          <div class="generate-header">
            <h4>AI Generate</h4>
            <p>{{ generateContextHint }}</p>
          </div>

          <!-- Context Selector -->
          <div class="context-selector">
            <div class="context-row">
              <label>Exam</label>
              <select v-model="selectedExam" @change="onExamChange" class="context-select">
                <option value="">Select Exam</option>
                <option v-for="exam in exams" :key="exam.id" :value="exam.id">{{ exam.name }}</option>
              </select>
            </div>
            <div class="context-row" v-if="selectedExam">
              <label>Subject</label>
              <select v-model="selectedSubject" @change="onSubjectChange" class="context-select">
                <option value="">{{ subjects.length ? 'All Subjects' : 'No subjects yet' }}</option>
                <option v-for="subject in subjects" :key="subject.id" :value="subject.id">{{ subject.name }}</option>
              </select>
            </div>
            <div class="context-row" v-if="selectedSubject">
              <label>Topic</label>
              <select v-model="selectedTopic" @change="onTopicChange" class="context-select">
                <option value="">{{ topics.length ? 'All Topics' : 'No topics yet' }}</option>
                <option v-for="topic in topics" :key="topic.id" :value="topic.id">{{ topic.name }}</option>
              </select>
            </div>
            <div class="context-row" v-if="selectedTopic">
              <label>Lesson</label>
              <select v-model="selectedLesson" @change="onLessonChange" class="context-select">
                <option value="">{{ lessons.length ? 'All Lessons' : 'No lessons yet' }}</option>
                <option v-for="lesson in lessons" :key="lesson.id" :value="lesson.id">{{ lesson.title }}</option>
              </select>
            </div>
            <div class="context-row" v-if="selectedExam">
              <label>Language</label>
              <select v-model="selectedLanguage" class="context-select">
                <option value="en">English</option>
                <option v-for="lang in LANGUAGE_OPTIONS.filter(l => l.value !== 'en')" :key="lang.value" :value="lang.value">{{ lang.label }}</option>
              </select>
            </div>
          </div>

          <!-- Current Context Display -->
          <div v-if="selectedExam" class="context-display">
            <span class="context-label">Generating for:</span>
            <span class="context-path">{{ currentContextPath }}</span>
          </div>

          <!-- Generation Actions - Dynamic based on selection -->
          <div class="generate-actions-section" v-if="selectedExam">
            <div class="actions-label">Available Actions</div>
            <div class="generate-actions">
              <button v-for="action in availableGenerateActions" :key="action.type"
                class="generate-btn" :class="{ 'primary': action.primary, 'active': generatingType === action.type }"
                @click="generateContentInline(action)"
                :disabled="generating">
                <vue-feather :type="generating && generatingType === action.type ? 'loader' : action.icon" 
                  size="16" :class="{ 'spin': generating && generatingType === action.type }" />
                <span>{{ action.label }}</span>
              </button>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="generating" class="generate-loading">
            <div class="loading-spinner">
              <vue-feather type="cpu" size="24" class="spin" />
            </div>
            <div class="loading-text">{{ generatingText }}</div>
            <div class="loading-dots"><span></span><span></span><span></span></div>
          </div>

          <!-- Generated Content Display -->
          <div v-if="generatedContent && !generating" class="generated-content-section">
            <div class="generated-header">
              <div class="generated-title">
                <vue-feather :type="generatedContentType?.icon || 'file-text'" size="18" />
                <span>{{ generatedContentType?.title || 'Generated Content' }}</span>
                <span class="generated-count" v-if="generatedItems.length">{{ generatedItems.length }} items</span>
              </div>
              <div class="generated-actions">
                <button @click="clearGeneratedContent" class="gen-action-btn" title="Clear">
                  <vue-feather type="x" size="14" />
                </button>
                <button @click="copyGeneratedContent" class="gen-action-btn" title="Copy">
                  <vue-feather type="copy" size="14" />
                </button>
              </div>
            </div>

            <!-- Subjects List -->
            <div v-if="generatedContentType?.type === 'subjects'" class="content-list subjects-list">
              <div v-for="(item, idx) in generatedItems" :key="idx" class="content-item subject-item">
                <div class="item-header">
                  <span class="item-number">{{ idx + 1 }}</span>
                  <span class="item-name">{{ item.name }}</span>
                  <span v-if="item.importance" :class="['importance-badge', item.importance]">{{ item.importance }}</span>
                </div>
                <p v-if="item.description" class="item-desc">{{ item.description }}</p>
                <div v-if="item.estimated_hours" class="item-meta">
                  <vue-feather type="clock" size="12" /> {{ item.estimated_hours }} hours
                </div>
              </div>
            </div>

            <!-- Topics List -->
            <div v-else-if="generatedContentType?.type === 'topics'" class="content-list topics-list">
              <div v-for="(item, idx) in generatedItems" :key="idx" class="content-item topic-item">
                <div class="item-header">
                  <span class="item-number">{{ idx + 1 }}</span>
                  <span class="item-name">{{ item.name }}</span>
                  <span v-if="item.importance" :class="['importance-badge', item.importance]">{{ item.importance }}</span>
                </div>
                <p v-if="item.description" class="item-desc">{{ item.description }}</p>
                <div class="item-meta" v-if="item.estimated_hours || item.key_concepts">
                  <span v-if="item.estimated_hours"><vue-feather type="clock" size="12" /> {{ item.estimated_hours }}h</span>
                  <span v-if="item.key_concepts" class="concepts">{{ item.key_concepts?.slice(0, 3).join(', ') }}</span>
                </div>
              </div>
            </div>

            <!-- Lessons List -->
            <div v-else-if="generatedContentType?.type === 'lessons'" class="content-list lessons-list">
              <div v-for="(item, idx) in generatedItems" :key="idx" class="content-item lesson-item">
                <div class="item-header">
                  <span class="item-number">{{ idx + 1 }}</span>
                  <span class="item-name">{{ item.title || item.name }}</span>
                  <span v-if="item.duration_minutes || item.duration" class="duration-badge">
                    {{ item.duration_minutes || item.duration }} min
                  </span>
                </div>
                <p v-if="item.description" class="item-desc">{{ item.description }}</p>
                <div v-if="item.learning_objectives" class="objectives">
                  <strong>Objectives:</strong>
                  <ul>
                    <li v-for="(obj, i) in item.learning_objectives?.slice(0, 3)" :key="i">{{ obj }}</li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Questions List -->
            <div v-else-if="generatedContentType?.type === 'questions'" class="content-list questions-list">
              <div v-for="(item, idx) in generatedItems" :key="idx" class="content-item question-item">
                <div class="question-header">
                  <span class="q-number">Q{{ idx + 1 }}</span>
                  <span v-if="item.difficulty" :class="['difficulty-badge', item.difficulty]">{{ item.difficulty }}</span>
                </div>
                <p class="question-text">{{ item.question_text }}</p>
                <div class="options-grid" v-if="item.options">
                  <div v-for="(opt, key) in item.options" :key="key" 
                    :class="['option', { 'correct': item.correct_answer === key }]">
                    <span class="opt-key">{{ key.toUpperCase() }}</span>
                    <span class="opt-text">{{ opt }}</span>
                    <vue-feather v-if="item.correct_answer === key" type="check" size="14" class="correct-icon" />
                  </div>
                </div>
                <div v-if="item.explanation" class="explanation">
                  <strong>Explanation:</strong> {{ item.explanation }}
                </div>
                <div v-if="item.shortcut_method" class="shortcut-method">
                  <div class="shortcut-header">
                    <vue-feather type="zap" size="14" />
                    <strong>Shortcut Method:</strong>
                  </div>
                  <p>{{ item.shortcut_method }}</p>
                </div>
              </div>
            </div>

            <!-- Previous Year Questions List -->
            <div v-else-if="generatedContentType?.type === 'pyq'" class="content-list pyq-list">
              <div class="pyq-info-banner">
                <vue-feather type="archive" size="16" />
                <span>Questions from Previous 25 Years (2000-2024)</span>
              </div>
              <div v-for="(item, idx) in generatedItems" :key="idx" class="content-item question-item pyq-item">
                <div class="question-header">
                  <span class="q-number">Q{{ idx + 1 }}</span>
                  <span v-if="item.year_asked" class="year-badge">
                    <vue-feather type="calendar" size="12" />
                    {{ item.year_asked }}
                  </span>
                  <span v-if="item.subject" class="subject-badge">{{ item.subject }}</span>
                  <span v-if="item.topic" class="topic-badge">{{ item.topic }}</span>
                  <span v-if="item.difficulty" :class="['difficulty-badge', item.difficulty]">{{ item.difficulty }}</span>
                </div>
                <p class="question-text">{{ item.question_text }}</p>
                <div class="options-grid" v-if="item.options">
                  <div v-for="(opt, key) in item.options" :key="key" 
                    :class="['option', { 'correct': item.correct_answer === key }]">
                    <span class="opt-key">{{ key.toUpperCase() }}</span>
                    <span class="opt-text">{{ opt }}</span>
                    <vue-feather v-if="item.correct_answer === key" type="check" size="14" class="correct-icon" />
                  </div>
                </div>
                <div v-if="item.explanation" class="explanation">
                  <strong>Explanation:</strong> {{ item.explanation }}
                </div>
                <div v-if="item.shortcut_method" class="shortcut-method">
                  <div class="shortcut-header">
                    <vue-feather type="zap" size="14" />
                    <strong>Shortcut Method:</strong>
                  </div>
                  <p>{{ item.shortcut_method }}</p>
                </div>
              </div>
            </div>

            <!-- Full Mock Test -->
            <div v-else-if="generatedContentType?.type === 'mock_test'" class="content-list mock-test-structure">
              <div class="mock-test-header" v-if="generatedTestMeta">
                <h4 class="mock-test-title">{{ generatedTestMeta.title || 'Full Mock Test' }}</h4>
                <p v-if="generatedTestMeta.description" class="mock-test-desc">{{ generatedTestMeta.description }}</p>
                <div class="mock-test-meta">
                  <div class="meta-item"><vue-feather type="clock" size="14" /> {{ generatedTestMeta.duration || 180 }} mins</div>
                  <div class="meta-item"><vue-feather type="help-circle" size="14" /> {{ generatedTestMeta.total_questions || generatedItems.length }} questions</div>
                  <div class="meta-item"><vue-feather type="award" size="14" /> {{ generatedTestMeta.total_marks || generatedItems.length }} marks</div>
                  <div class="meta-item" v-if="generatedTestMeta.passing_marks"><vue-feather type="check-circle" size="14" /> Pass: {{ generatedTestMeta.passing_marks }}</div>
                  <div class="meta-item negative" v-if="generatedTestMeta.negative_marking"><vue-feather type="minus-circle" size="14" /> -{{ generatedTestMeta.negative_marking }} per wrong</div>
                </div>
                <div v-if="generatedTestMeta.sections && generatedTestMeta.sections.length" class="sections-breakdown">
                  <strong>Sections:</strong>
                  <div class="sections-list">
                    <span v-for="(section, si) in generatedTestMeta.sections" :key="si" class="section-chip">
                      {{ section.name }} ({{ section.questions_count }}Q × {{ section.marks_per_question }}M)
                    </span>
                  </div>
                </div>
                <div v-if="generatedTestMeta.instructions && generatedTestMeta.instructions.length" class="instructions-box">
                  <strong>Instructions:</strong>
                  <ul>
                    <li v-for="(inst, ii) in generatedTestMeta.instructions" :key="ii">{{ inst }}</li>
                  </ul>
                </div>
              </div>
              <div v-for="(item, idx) in generatedItems" :key="idx" class="content-item question-item mock-question">
                <div class="question-header">
                  <span class="q-number">Q{{ idx + 1 }}</span>
                  <span v-if="item.section" class="section-badge">{{ item.section }}</span>
                  <span v-if="item.difficulty" :class="['difficulty-badge', item.difficulty]">{{ item.difficulty }}</span>
                  <span class="marks-badge" v-if="item.marks">{{ item.marks }} mark{{ item.marks > 1 ? 's' : '' }}</span>
                </div>
                <p class="question-text">{{ item.question_text }}</p>
                <div class="options-grid" v-if="item.options">
                  <div v-for="(opt, key) in item.options" :key="key" 
                    :class="['option', { 'correct': item.correct_answer === key }]">
                    <span class="opt-key">{{ key.toUpperCase() }}</span>
                    <span class="opt-text">{{ opt }}</span>
                    <vue-feather v-if="item.correct_answer === key" type="check" size="14" class="correct-icon" />
                  </div>
                </div>
                <div v-if="item.explanation" class="explanation">
                  <strong>Explanation:</strong> {{ item.explanation }}
                </div>
                <div v-if="item.shortcut_method" class="shortcut-method">
                  <div class="shortcut-header">
                    <vue-feather type="zap" size="14" />
                    <strong>Shortcut Method:</strong>
                  </div>
                  <p>{{ item.shortcut_method }}</p>
                </div>
              </div>
            </div>

            <!-- Flashcards List -->
            <div v-else-if="generatedContentType?.type === 'flashcards'" class="content-list flashcards-list">
              <div v-for="(item, idx) in generatedItems" :key="idx" class="content-item flashcard-item"
                @click="toggleFlashcard(idx)">
                <div class="flashcard-inner" :class="{ 'flipped': flippedCards.includes(idx) }">
                  <div class="flashcard-front">
                    <span class="card-number">{{ idx + 1 }}</span>
                    <p>{{ item.front }}</p>
                    <span class="flip-hint">Click to flip</span>
                  </div>
                  <div class="flashcard-back">
                    <p>{{ item.back }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Flashcard Deck (with deck meta + cards) -->
            <div v-else-if="generatedContentType?.type === 'flashcard_deck'" class="content-list flashcard-deck-section">
              <div v-if="generatedTestMeta?.deck_title" class="deck-meta-header">
                <h4 class="deck-meta-title">📚 {{ generatedTestMeta.deck_title }}</h4>
                <p v-if="generatedTestMeta.deck_description" class="deck-meta-desc">{{ generatedTestMeta.deck_description }}</p>
                <div class="deck-meta-stats">
                  <span class="meta-item"><vue-feather type="layers" size="14" /> {{ generatedItems.length }} cards</span>
                </div>
              </div>
              <div class="flashcards-list">
                <div v-for="(item, idx) in generatedItems" :key="idx" class="content-item flashcard-item"
                  @click="toggleFlashcard(idx)">
                  <div class="flashcard-inner" :class="{ 'flipped': flippedCards.includes(idx) }">
                    <div class="flashcard-front">
                      <span class="card-number">{{ idx + 1 }}</span>
                      <p>{{ item.front }}</p>
                      <span v-if="item.difficulty" :class="['difficulty-badge', item.difficulty]" style="position:absolute;top:8px;right:8px;">{{ item.difficulty }}</span>
                      <span class="flip-hint">Click to flip</span>
                    </div>
                    <div class="flashcard-back">
                      <p>{{ item.back }}</p>
                      <span v-if="item.hint" class="flashcard-hint">💡 {{ item.hint }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Notes/Content (Markdown) -->
            <div v-else-if="generatedContentType?.type === 'notes' || generatedContentType?.type === 'content'" 
              class="content-markdown">
              <div class="markdown-content" v-html="renderMarkdown(generatedContent)"></div>
            </div>

            <!-- Test Structure -->
            <div v-else-if="generatedContentType?.type === 'test'" class="content-list test-structure">
              <div class="test-meta" v-if="generatedTestMeta">
                <div class="meta-item"><vue-feather type="clock" size="14" /> {{ generatedTestMeta.duration || 30 }} mins</div>
                <div class="meta-item"><vue-feather type="help-circle" size="14" /> {{ generatedTestMeta.total_questions || generatedItems.length }} questions</div>
                <div class="meta-item"><vue-feather type="award" size="14" /> {{ generatedTestMeta.total_marks || generatedItems.length }} marks</div>
              </div>
              <div v-for="(item, idx) in generatedItems" :key="idx" class="content-item question-item">
                <div class="question-header">
                  <span class="q-number">Q{{ idx + 1 }}</span>
                  <span v-if="item.difficulty" :class="['difficulty-badge', item.difficulty]">{{ item.difficulty }}</span>
                  <span class="marks-badge" v-if="item.marks">{{ item.marks }} mark{{ item.marks > 1 ? 's' : '' }}</span>
                </div>
                <p class="question-text">{{ item.question_text }}</p>
                <div class="options-grid" v-if="item.options">
                  <div v-for="(opt, key) in item.options" :key="key" 
                    :class="['option', { 'correct': item.correct_answer === key }]">
                    <span class="opt-key">{{ key.toUpperCase() }}</span>
                    <span class="opt-text">{{ opt }}</span>
                  </div>
                </div>
                <div v-if="item.explanation" class="explanation">
                  <strong>Explanation:</strong> {{ item.explanation }}
                </div>
                <div v-if="item.shortcut_method" class="shortcut-method">
                  <div class="shortcut-header">
                    <vue-feather type="zap" size="14" />
                    <strong>Shortcut Method:</strong>
                  </div>
                  <p>{{ item.shortcut_method }}</p>
                </div>
              </div>
            </div>

            <!-- Syllabus Structure -->
            <div v-else-if="generatedContentType?.type === 'syllabus'" class="content-list syllabus-structure">
              <div v-for="(item, idx) in generatedItems" :key="idx" class="content-item syllabus-item">
                <div class="syllabus-topic">
                  <span class="item-number">{{ idx + 1 }}</span>
                  <span class="item-name">{{ item.name }}</span>
                  <span v-if="item.estimated_hours" class="hours-badge">{{ item.estimated_hours }}h</span>
                </div>
                <p v-if="item.description" class="item-desc">{{ item.description }}</p>
                <div v-if="item.subtopics && item.subtopics.length" class="subtopics">
                  <div v-for="(sub, si) in item.subtopics" :key="si" class="subtopic">
                    <span class="sub-bullet">•</span>
                    <span>{{ sub.name || sub }}</span>
                  </div>
                </div>
                <div v-if="item.learning_objectives" class="objectives">
                  <div v-for="(obj, oi) in item.learning_objectives?.slice(0, 2)" :key="oi" class="objective">
                    <vue-feather type="check-circle" size="12" /> {{ obj }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Generic/Fallback -->
            <div v-else class="content-markdown">
              <div v-if="generatedItems.length === 0 && generatedContent" class="parse-warning">
                <vue-feather type="alert-circle" size="14" />
                <span>Content couldn't be parsed into structured items. Showing raw content below.</span>
              </div>
              <div class="markdown-content" v-html="renderMarkdown(generatedContent)"></div>
            </div>

            <!-- Save Actions -->
            <div class="save-actions-bar">
              <button @click="openSaveModalFromGenerated" class="save-all-btn" 
                v-if="generatedItems.length || generatedContentType?.type === 'notes' || generatedContentType?.type === 'content'">
                <vue-feather type="save" size="16" />
                <span v-if="generatedContentType?.type === 'flashcard_deck'">Save Deck ({{ generatedItems.length }} cards)</span>
                <span v-else-if="generatedItems.length">Save {{ generatedItems.length }} {{ generatedContentType?.title || 'Items' }}</span>
                <span v-else>Save {{ generatedContentType?.title || 'Content' }}</span>
              </button>
              <button @click="regenerateContent" class="regenerate-btn">
                <vue-feather type="refresh-cw" size="16" />
                Regenerate
              </button>
            </div>
          </div>

          <!-- No Selection State -->
          <div v-if="!selectedExam" class="no-selection">
            <vue-feather type="arrow-up" size="24" />
            <p>Select an exam to start generating content</p>
          </div>
        </div>

        <!-- CHAT MODE: Regular Chat -->
        <template v-if="activeMode === 'chat'">
          <!-- Quick Actions -->
          <TransitionGroup name="quick-action" tag="div" class="quick-actions" v-if="messages.length === 0 && attachments.length === 0">
            <button v-for="(action, i) in quickActions" :key="action.prompt" 
              @click="sendQuickAction(action.prompt)" 
              class="quick-btn"
              :style="{ animationDelay: `${i * 0.08}s` }">
              <vue-feather :type="action.icon" size="14" />
              <span>{{ action.label }}</span>
            </button>
          </TransitionGroup>

          <!-- Chat Body -->
          <div class="chatbox-body" ref="chatContainer">
            <!-- Welcome Message -->
            <Transition name="welcome">
              <div v-if="messages.length === 0" class="welcome-message">
                <div class="welcome-icon">
                  <vue-feather type="cpu" size="32" />
                </div>
                <h4>Welcome to TestPal AI</h4>
                <p>Ask me anything or use Create/Generate modes to build content.</p>
              </div>
            </Transition>

            <!-- Messages -->
            <TransitionGroup name="message" tag="div" class="messages-container">
              <div v-for="(msg, index) in messages" :key="msg.id || index" 
                :class="['message', msg.role, { 'sending': msg.sending }]">
                <div class="message-avatar" :class="{ 'pop': msg.isNew }">
                  <vue-feather :type="msg.role === 'user' ? 'user' : 'cpu'" size="14" />
                </div>
                <div class="message-content">
                  <TransitionGroup name="attachment-chip" tag="div" v-if="msg.attachments && msg.attachments.length" class="message-attachments">
                    <div v-for="(file, i) in msg.attachments" :key="i" class="attachment-chip">
                      <vue-feather type="paperclip" size="12" />
                      <span>{{ file.name }}</span>
                    </div>
                  </TransitionGroup>
                  <div class="message-text" 
                    :class="{ 'markdown-content': msg.role === 'assistant', 'typewriter': msg.isNew && msg.role === 'assistant' }"
                    v-html="msg.role === 'assistant' ? renderMarkdown(msg.content) : escapeHtml(msg.content)">
                  </div>
                  <!-- Action Buttons for AI responses -->
                  <div v-if="msg.role === 'assistant' && msg.actions && msg.actions.length" class="message-actions">
                    <button v-for="action in msg.actions" :key="action.label" 
                      @click="executeAction(action, msg)" class="msg-action-btn"
                      :class="action.variant || 'primary'">
                      <vue-feather :type="action.icon" size="12" />
                      {{ action.label }}
                    </button>
                  </div>
                  <div class="message-time">{{ formatTime(msg.timestamp) }}</div>
                </div>
                <button v-if="msg.role === 'assistant'" @click="copyMessage(msg.content)" class="copy-btn" title="Copy">
                  <vue-feather type="copy" size="12" />
                </button>
              </div>
            </TransitionGroup>

            <!-- Thinking Indicator -->
            <Transition name="thinking">
              <div v-if="loading" class="message assistant thinking-message">
                <div class="message-avatar thinking-avatar">
                  <vue-feather type="cpu" size="14" />
                </div>
                <div class="message-content">
                  <div class="thinking-indicator">
                    <div class="thinking-text">{{ loadingText }}</div>
                    <div class="thinking-dots">
                      <span></span><span></span><span></span>
                    </div>
                  </div>
                </div>
              </div>
            </Transition>
          </div>
        </template>

        <!-- Attachments Preview -->
        <TransitionGroup name="attachment" tag="div" v-if="attachments.length && activeMode === 'chat'" class="attachments-preview">
          <div v-for="(file, index) in attachments" :key="file.name + index" class="attachment-item">
            <vue-feather type="file" size="14" />
            <span class="attachment-name">{{ file.name }}</span>
            <button @click="removeAttachment(index)" class="attachment-remove">
              <vue-feather type="x" size="12" />
            </button>
          </div>
        </TransitionGroup>

        <!-- Footer (Chat Mode) -->
        <div v-if="activeMode === 'chat'" class="chatbox-footer" :class="{ 'sending': loading }">
          <button v-if="sessionId" @click="clearChat" class="action-btn clear-btn" title="Clear chat">
            <vue-feather type="trash-2" size="14" />
          </button>
          <button @click="triggerFileInput" class="action-btn attach-btn" title="Attach file" :disabled="loading">
            <vue-feather type="paperclip" size="14" />
          </button>
          <input type="file" ref="fileInput" @change="handleFileSelect" multiple class="file-input"
            accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.csv,.xlsx" />
          <form @submit.prevent="sendMessage" class="input-form">
            <textarea v-model="inputMessage" class="chat-input" rows="1" placeholder="Ask anything or describe what to create..."
              @keydown.enter.exact.prevent="sendMessage" @input="autoResize" ref="inputField"
              :disabled="loading"></textarea>
            <button type="submit" class="send-btn" :class="{ 'ready': inputMessage.trim() || attachments.length }"
              :disabled="loading || (!inputMessage.trim() && !attachments.length)">
              <Transition name="send-icon" mode="out-in">
                <vue-feather v-if="!loading" type="send" size="16" key="send" />
                <div v-else class="send-loader" key="loader"></div>
              </Transition>
            </button>
          </form>
        </div>
      </div>
    </Transition>

    <!-- Create Modal -->
    <Transition name="modal">
      <div v-if="showCreateModal" class="create-modal-overlay" @click.self="closeCreateModal">
        <div class="create-modal">
          <div class="modal-header" :style="{ background: activeCreateCard?.gradient }">
            <vue-feather :type="activeCreateCard?.icon" size="24" />
            <h3>{{ activeCreateCard?.title }}</h3>
            <button @click="closeCreateModal" class="modal-close">
              <vue-feather type="x" size="20" />
            </button>
          </div>
          <div class="modal-body">
            <div v-for="field in activeCreateCard?.fields" :key="field.name" class="form-group">
              <label>{{ field.label }} <span v-if="field.required" class="required">*</span></label>
              <input v-if="field.type === 'text'" v-model="createForm[field.name]" 
                :placeholder="field.placeholder" class="form-input" />
              <textarea v-else-if="field.type === 'textarea'" v-model="createForm[field.name]"
                :placeholder="field.placeholder" class="form-textarea" rows="3"></textarea>
              <select v-else-if="field.type === 'select'" v-model="createForm[field.name]" class="form-select"
                @change="handleSelectChange(field)">
                <option value="">{{ field.placeholder }}</option>
                <option v-for="opt in getFieldOptions(field)" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
              </select>
              <input v-else-if="field.type === 'number'" v-model.number="createForm[field.name]" 
                type="number" :placeholder="field.placeholder" class="form-input" />
            </div>
            <div class="ai-assist-section">
              <button @click="aiAssistCreate" class="ai-assist-btn" :disabled="creatingWithAI">
                <vue-feather type="cpu" size="16" />
                {{ creatingWithAI ? 'Generating...' : 'AI Assist' }}
              </button>
              <span class="ai-hint">Let AI help fill in the details</span>
            </div>
          </div>
          <div class="modal-footer">
            <button @click="closeCreateModal" class="btn-cancel">Cancel</button>
            <button @click="submitCreate" class="btn-create" :disabled="!canSubmitCreate || loading">
              <vue-feather type="plus" size="16" />
              Create {{ activeCreateCard?.title }}
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Save Content Modal -->
    <Transition name="modal">
      <div v-if="showSaveModal" class="create-modal-overlay" @click.self="closeSaveModal">
        <div class="create-modal save-modal">
          <div class="modal-header" :style="{ background: saveModalConfig.gradient }">
            <vue-feather :type="saveModalConfig.icon" size="24" />
            <h3>{{ saveModalConfig.title }}</h3>
            <button @click="closeSaveModal" class="modal-close">
              <vue-feather type="x" size="20" />
            </button>
          </div>
          <div class="modal-body">
            <!-- Target Selection -->
            <div class="save-target-section">
              <h4>Save To</h4>
              <div class="target-selectors">
                <div class="form-group" v-if="saveModalConfig.requiresExam">
                  <label>Exam <span class="required">*</span></label>
                  <select v-model="saveTarget.examId" @change="onSaveExamChange" class="form-select">
                    <option value="">Select Exam</option>
                    <option v-for="exam in exams" :key="exam.id" :value="exam.id">{{ exam.name }}</option>
                  </select>
                </div>
                <div class="form-group" v-if="saveModalConfig.requiresSubject && saveTarget.examId">
                  <label>Subject <span class="required">*</span></label>
                  <select v-model="saveTarget.subjectId" @change="onSaveSubjectChange" class="form-select">
                    <option value="">Select Subject</option>
                    <option v-for="subject in saveSubjects" :key="subject.id" :value="subject.id">{{ subject.name }}</option>
                  </select>
                </div>
                <div class="form-group" v-if="saveModalConfig.requiresTopic && saveTarget.subjectId">
                  <label>Topic <span class="required">*</span></label>
                  <select v-model="saveTarget.topicId" @change="onSaveTopicChange" class="form-select">
                    <option value="">Select Topic</option>
                    <option v-for="topic in saveTopics" :key="topic.id" :value="topic.id">{{ topic.name }}</option>
                  </select>
                </div>
                <div class="form-group" v-if="saveModalConfig.requiresLesson && saveTarget.topicId">
                  <label>Lesson</label>
                  <select v-model="saveTarget.lessonId" class="form-select">
                    <option value="">Select Lesson (Optional)</option>
                    <option v-for="lesson in saveLessons" :key="lesson.id" :value="lesson.id">{{ lesson.title }}</option>
                  </select>
                </div>
                <div class="form-group" v-if="saveModalConfig.requiresTest">
                  <label>Test <span v-if="!saveTarget.topicId" class="required">*</span></label>
                  <select v-model="saveTarget.testId" class="form-select">
                    <option value="">{{ saveTests.length ? 'Select Test' : 'No tests available' }}</option>
                    <option v-for="test in saveTests" :key="test.id" :value="test.id">{{ test.title }}</option>
                  </select>
                  <div v-if="!saveTarget.testId && saveTarget.topicId && saveModalConfig.contentType === 'questions'" class="auto-create-hint">
                    <vue-feather type="info" size="12" />
                    <span>A new test will be auto-created for these questions</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Content Preview -->
            <div class="content-preview-section">
              <div class="preview-header">
                <h4>Content Preview</h4>
                <span class="preview-count" v-if="saveModalConfig.contentType !== 'notes' && saveModalConfig.contentType !== 'content'">
                  {{ parsedContent.length }} items
                </span>
                <span class="preview-count" v-else>Notes/Content</span>
              </div>
              
              <!-- Notes/Content Preview -->
              <div v-if="saveModalConfig.contentType === 'notes' || saveModalConfig.contentType === 'content'" class="notes-preview">
                <!-- Save Location Indicator -->
                <div class="save-location-indicator">
                  <vue-feather :type="saveTarget.lessonId ? 'file-text' : 'folder'" size="14" />
                  <span v-if="saveTarget.lessonId">
                    Will be saved as <strong>lesson content</strong> for "{{ saveLessons.find(l => l.id === saveTarget.lessonId)?.title || 'Selected Lesson' }}"
                  </span>
                  <span v-else>
                    Will be saved as <strong>topic notes</strong> for "{{ saveTopics.find(t => t.id === saveTarget.topicId)?.name || 'Selected Topic' }}"
                  </span>
                </div>
                <div class="notes-preview-content" v-html="renderMarkdown(parsedContent[0]?.content || '')"></div>
              </div>
              
              <!-- Regular Items Preview -->
              <div v-else class="preview-list">
                <div v-for="(item, index) in parsedContent.slice(0, previewLimit)" :key="index" 
                  class="preview-item" :class="{ 'selected': selectedItems.includes(index) }">
                  <label class="preview-checkbox">
                    <input type="checkbox" v-model="selectedItems" :value="index" />
                    <span class="checkmark"></span>
                  </label>
                  <div class="preview-content">
                    <div class="preview-title">{{ getPreviewTitle(item, index) }}</div>
                    <div class="preview-subtitle" v-if="item.description || item.explanation">
                      {{ truncateText(item.description || item.explanation, 80) }}
                    </div>
                  </div>
                  <span v-if="item.difficulty" :class="['difficulty-badge', item.difficulty]">
                    {{ item.difficulty }}
                  </span>
                </div>
                <div v-if="parsedContent.length > previewLimit" class="preview-more">
                  <button @click="previewLimit += 10" class="show-more-btn">
                    Show {{ Math.min(10, parsedContent.length - previewLimit) }} more...
                  </button>
                </div>
              </div>
              <div class="preview-actions" v-if="saveModalConfig.contentType !== 'notes' && saveModalConfig.contentType !== 'content'">
                <button @click="selectAllItems" class="select-btn">
                  {{ selectedItems.length === parsedContent.length ? 'Deselect All' : 'Select All' }}
                </button>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button @click="closeSaveModal" class="btn-cancel">Cancel</button>
            <button @click="saveContent" class="btn-create" :disabled="!canSaveContent || savingContent">
              <vue-feather :type="savingContent ? 'loader' : 'save'" size="16" :class="{ 'spin': savingContent }" />
              <span v-if="saveModalConfig.contentType === 'notes' || saveModalConfig.contentType === 'content'">
                {{ savingContent ? 'Saving...' : 'Save Notes' }}
              </span>
              <span v-else>
                {{ savingContent ? 'Saving...' : `Save ${selectedItems.length} Items` }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Floating Button -->
    <button @click="toggleChat" class="ai-fab" :class="{ 'is-open': isOpen, 'has-messages': messages.length > 0 && !isOpen }"
      title="TestPal AI">
      <Transition name="fab-icon" mode="out-in">
        <vue-feather v-if="!isOpen" type="cpu" size="24" key="cpu" />
        <vue-feather v-else type="x" size="24" key="close" />
      </Transition>
      <span class="fab-badge" v-if="!isOpen">AI</span>
      <span class="fab-ring" v-if="!isOpen"></span>
    </button>
  </div>
</template>


<script>
import { ref, reactive, nextTick, onMounted, watch, computed } from 'vue'
import api from '@/services/api'
import { marked } from 'marked'
import katex from 'katex'
import 'katex/dist/katex.min.css'
import { useNotifications } from '@/composables/useNotifications'

const STORAGE_KEY = 'testpal_ai_chat'

// Content type configurations for parsing and saving
const CONTENT_TYPES = {
  questions: {
    icon: 'help-circle',
    gradient: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
    title: 'Save Questions',
    requiresTest: true,
    requiresExam: true,
    requiresSubject: true,
    requiresTopic: true
  },
  subjects: {
    icon: 'book',
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    title: 'Save Subjects',
    requiresExam: true
  },
  topics: {
    icon: 'hash',
    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    title: 'Save Topics',
    requiresExam: true,
    requiresSubject: true
  },
  lessons: {
    icon: 'file-text',
    gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    title: 'Save Lessons',
    requiresExam: true,
    requiresSubject: true,
    requiresTopic: true
  },
  flashcards: {
    icon: 'layers',
    gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    title: 'Save Flashcards',
    requiresExam: true,
    requiresSubject: true,
    requiresTopic: true
  },
  flashcard_deck: {
    icon: 'layers',
    gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    title: 'Save Flashcard Deck',
    requiresExam: true,
    requiresSubject: false,
    requiresTopic: false
  },
  notes: {
    icon: 'edit-3',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    title: 'Save Notes',
    requiresExam: true,
    requiresSubject: true,
    requiresTopic: true,
    requiresLesson: false
  },
  content: {
    icon: 'file-text',
    gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    title: 'Save Content',
    requiresExam: true,
    requiresSubject: true,
    requiresTopic: true,
    requiresLesson: false
  },
  test: {
    icon: 'clipboard',
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    title: 'Save Test',
    requiresExam: true,
    requiresSubject: true,
    requiresTopic: true,
    requiresLesson: false
  }
}

export default {
  name: 'TestPalAIChatbox',
  setup() {
    // Notifications
    const { toast } = useNotifications()
    
    // Core state
    const isOpen = ref(false)
    const isExpanded = ref(false)
    const activeMode = ref('chat')
    const messages = ref([])
    const inputMessage = ref('')
    const loading = ref(false)
    const loadingText = ref('Thinking')
    const sessionId = ref(null)
    const aiStatus = ref('online')
    const chatContainer = ref(null)
    const inputField = ref(null)
    const fileInput = ref(null)
    const attachments = ref([])

    // Create Mode state
    const showCreateModal = ref(false)
    const activeCreateCard = ref(null)
    const createForm = reactive({})
    const creatingWithAI = ref(false)

    // Save Modal state
    const showSaveModal = ref(false)
    const saveModalConfig = ref({})
    const parsedContent = ref([])
    const selectedItems = ref([])
    const previewLimit = ref(5)
    const savingContent = ref(false)
    const saveTarget = reactive({
      examId: '',
      subjectId: '',
      topicId: '',
      lessonId: '',
      testId: ''
    })
    const saveSubjects = ref([])
    const saveTopics = ref([])
    const saveLessons = ref([])
    const saveTests = ref([])
    const currentMessageData = ref(null)

    // Generate Mode - Context
    const exams = ref([])
    const subjects = ref([])
    const topics = ref([])
    const lessons = ref([])
    const selectedExam = ref('')
    const selectedSubject = ref('')
    const selectedTopic = ref('')
    const selectedLesson = ref('')
    const selectedLanguage = ref('en')

    // Generate Mode - Inline Content
    const generating = ref(false)
    const generatingType = ref('')
    const generatingText = ref('Generating content')
    const generatedContent = ref('')
    const generatedItems = ref([])
    const generatedContentType = ref(null)
    const generatedTestMeta = ref(null)
    const flippedCards = ref([])
    const lastGenerateAction = ref(null)

    // Options data for selects
    const allSubjects = ref([])
    const allTopics = ref([])
    const allLessons = ref([])
    const allTests = ref([])
    const allFlashcardDecks = ref([])

    // Quick Actions for Chat
    const quickActions = [
      { icon: 'help-circle', label: 'Generate MCQs', prompt: 'Generate 10 MCQ questions with answers for ' },
      { icon: 'file-text', label: 'Create Syllabus', prompt: 'Create a detailed syllabus for ' },
      { icon: 'book-open', label: 'Write Lesson', prompt: 'Write a comprehensive lesson on ' },
      { icon: 'layers', label: 'Flashcards', prompt: 'Create 20 flashcards for ' },
      { icon: 'list', label: 'Test Series', prompt: 'Design a test series plan for ' },
      { icon: 'edit-3', label: 'Study Notes', prompt: 'Create detailed study notes for ' }
    ]

    // Language options for content generation
    const LANGUAGE_OPTIONS = [
      { value: 'en', label: 'English' },
      { value: 'hi', label: 'Hindi (हिन्दी)' },
      { value: 'ta', label: 'Tamil (தமிழ்)' },
      { value: 'te', label: 'Telugu (తెలుగు)' },
      { value: 'kn', label: 'Kannada (ಕನ್ನಡ)' },
      { value: 'ml', label: 'Malayalam (മലയാളം)' },
      { value: 'mr', label: 'Marathi (मराठी)' },
      { value: 'bn', label: 'Bengali (বাংলা)' },
      { value: 'gu', label: 'Gujarati (ગુજરાતી)' },
      { value: 'pa', label: 'Punjabi (ਪੰਜਾਬੀ)' },
      { value: 'ur', label: 'Urdu (اردو)' },
      { value: 'or', label: 'Odia (ଓଡ଼ିଆ)' }
    ]

    // Create Cards - Direct content creation
    const createCards = [
      {
        type: 'exam',
        icon: 'award',
        title: 'Exam',
        description: 'Create a new exam category',
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        fields: [
          { name: 'name', label: 'Exam Name', type: 'text', placeholder: 'e.g., UPSC Civil Services', required: true },
          { name: 'category', label: 'Category', type: 'text', placeholder: 'e.g., Government, Banking', required: true },
          { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Describe the exam...' }
        ],
        endpoint: '/v1/admin/exams'
      },
      {
        type: 'subject',
        icon: 'book',
        title: 'Subject',
        description: 'Add a subject to an exam',
        gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        fields: [
          { name: 'exam_id', label: 'Exam', type: 'select', options: 'exams', placeholder: 'Select Exam', required: true },
          { name: 'name', label: 'Subject Name', type: 'text', placeholder: 'e.g., Indian History', required: true },
          { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Describe the subject...' }
        ],
        endpoint: '/v1/admin/subjects'
      },
      {
        type: 'topic',
        icon: 'hash',
        title: 'Topic',
        description: 'Add a topic to a subject',
        gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        fields: [
          { name: 'exam_id', label: 'Exam', type: 'select', options: 'exams', placeholder: 'Select Exam', required: true, triggers: 'subjects' },
          { name: 'subject_id', label: 'Subject', type: 'select', options: 'filteredSubjects', placeholder: 'Select Subject', required: true },
          { name: 'name', label: 'Topic Name', type: 'text', placeholder: 'e.g., Ancient India', required: true },
          { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Describe the topic...' },
          { name: 'language', label: 'Language', type: 'select', options: 'languages', placeholder: 'Select Language (default: English)' }
        ],
        endpoint: '/v1/admin/topics'
      },
      {
        type: 'lesson',
        icon: 'file-text',
        title: 'Lesson',
        description: 'Create a lesson with content',
        gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
        fields: [
          { name: 'exam_id', label: 'Exam', type: 'select', options: 'exams', placeholder: 'Select Exam', required: true, triggers: 'subjects' },
          { name: 'subject_id', label: 'Subject', type: 'select', options: 'filteredSubjects', placeholder: 'Select Subject', required: true, triggers: 'topics' },
          { name: 'topic_id', label: 'Topic', type: 'select', options: 'filteredTopics', placeholder: 'Select Topic', required: true },
          { name: 'title', label: 'Lesson Title', type: 'text', placeholder: 'e.g., Introduction to Vedic Period', required: true },
          { name: 'content', label: 'Content', type: 'textarea', placeholder: 'Lesson content (AI can help!)' },
          { name: 'duration', label: 'Duration (mins)', type: 'number', placeholder: '30' },
          { name: 'language', label: 'Language', type: 'select', options: 'languages', placeholder: 'Select Language (default: English)' }
        ],
        endpoint: '/v1/admin/lessons'
      },
      {
        type: 'test',
        icon: 'clipboard',
        title: 'Test',
        description: 'Create a test or quiz',
        gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
        fields: [
          { name: 'exam_id', label: 'Exam', type: 'select', options: 'exams', placeholder: 'Select Exam', required: true, triggers: 'subjects' },
          { name: 'subject_id', label: 'Subject', type: 'select', options: 'filteredSubjects', placeholder: 'Select Subject', triggers: 'topics' },
          { name: 'topic_id', label: 'Topic', type: 'select', options: 'filteredTopics', placeholder: 'Select Topic', required: true, triggers: 'lessons' },
          { name: 'lesson_id', label: 'Lesson (Optional)', type: 'select', options: 'filteredLessons', placeholder: 'Select Lesson' },
          { name: 'title', label: 'Test Title', type: 'text', placeholder: 'e.g., Chapter 1 Quiz', required: true },
          { name: 'duration_minutes', label: 'Duration (mins)', type: 'number', placeholder: '30', required: true },
          { name: 'total_marks', label: 'Total Marks', type: 'number', placeholder: '100' },
          { name: 'passing_marks', label: 'Passing Marks', type: 'number', placeholder: '40' },
          { name: 'negative_marking', label: 'Negative Marking', type: 'number', placeholder: '0.25' },
          { name: 'language', label: 'Language', type: 'select', options: 'languages', placeholder: 'Select Language (default: English)' }
        ],
        endpoint: '/v1/admin/tests'
      },
      {
        type: 'question',
        icon: 'help-circle',
        title: 'Question',
        description: 'Add questions to a test',
        gradient: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
        fields: [
          { name: 'test_id', label: 'Test', type: 'select', options: 'tests', placeholder: 'Select Test', required: true },
          { name: 'question_type', label: 'Question Type', type: 'select', options: 'questionTypes', placeholder: 'Select Type', required: true },
          { name: 'content', label: 'Question', type: 'textarea', placeholder: 'Enter your question...', required: true },
          { name: 'option_a', label: 'Option A', type: 'text', placeholder: 'First option' },
          { name: 'option_b', label: 'Option B', type: 'text', placeholder: 'Second option' },
          { name: 'option_c', label: 'Option C', type: 'text', placeholder: 'Third option' },
          { name: 'option_d', label: 'Option D', type: 'text', placeholder: 'Fourth option' },
          { name: 'correct_answer', label: 'Correct Answer', type: 'select', options: 'answers', placeholder: 'Select correct answer' },
          { name: 'explanation', label: 'Explanation', type: 'textarea', placeholder: 'Explain why this is correct...' },
          { name: 'difficulty', label: 'Difficulty', type: 'select', options: 'difficulty', placeholder: 'Select difficulty' },
          { name: 'marks', label: 'Marks', type: 'number', placeholder: '1' },
          { name: 'language', label: 'Language', type: 'select', options: 'languages', placeholder: 'Select Language (default: English)' }
        ],
        endpoint: '/v1/admin/questions'
      },
      {
        type: 'notes',
        icon: 'edit-3',
        title: 'Notes',
        description: 'Create study notes',
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        fields: [
          { name: 'exam_id', label: 'Exam', type: 'select', options: 'exams', placeholder: 'Select Exam', required: true, triggers: 'subjects' },
          { name: 'subject_id', label: 'Subject', type: 'select', options: 'filteredSubjects', placeholder: 'Select Subject', required: true, triggers: 'topics' },
          { name: 'topic_id', label: 'Topic', type: 'select', options: 'filteredTopics', placeholder: 'Select Topic', required: true },
          { name: 'title', label: 'Notes Title', type: 'text', placeholder: 'e.g., Key Concepts Summary', required: true },
          { name: 'content', label: 'Content', type: 'textarea', placeholder: 'Notes content...' },
          { name: 'language', label: 'Language', type: 'select', options: 'languages', placeholder: 'Select Language (default: English)' }
        ],
        endpoint: '/v1/admin/notes'
      },
      {
        type: 'flashcard_deck',
        icon: 'layers',
        title: 'Flashcard Deck',
        description: 'Create a flashcard deck',
        gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
        fields: [
          { name: 'exam_id', label: 'Exam', type: 'select', options: 'exams', placeholder: 'Select Exam', required: true, triggers: 'subjects' },
          { name: 'subject_id', label: 'Subject', type: 'select', options: 'filteredSubjects', placeholder: 'Select Subject', triggers: 'topics' },
          { name: 'topic_id', label: 'Topic', type: 'select', options: 'filteredTopics', placeholder: 'Select Topic', triggers: 'lessons' },
          { name: 'lesson_id', label: 'Lesson (Optional)', type: 'select', options: 'filteredLessons', placeholder: 'Select Lesson' },
          { name: 'title', label: 'Deck Title', type: 'text', placeholder: 'e.g., Algebra Formulas', required: true },
          { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Describe the deck...' },
          { name: 'color', label: 'Color', type: 'text', placeholder: '#6366F1' },
          { name: 'icon_emoji', label: 'Icon Emoji', type: 'text', placeholder: '📚' }
        ],
        endpoint: '/v1/admin/flashcard-decks'
      },
      {
        type: 'flashcard_card',
        icon: 'credit-card',
        title: 'Flashcard',
        description: 'Add a card to a deck',
        gradient: 'linear-gradient(135deg, #f5af19 0%, #f12711 100%)',
        fields: [
          { name: 'deck_id', label: 'Deck', type: 'select', options: 'flashcardDecks', placeholder: 'Select Deck', required: true },
          { name: 'front', label: 'Front (Question/Term)', type: 'textarea', placeholder: 'What is the quadratic formula?', required: true },
          { name: 'back', label: 'Back (Answer/Definition)', type: 'textarea', placeholder: 'x = (-b ± √(b² - 4ac)) / 2a', required: true },
          { name: 'hint', label: 'Hint (Optional)', type: 'text', placeholder: 'Think about the discriminant' },
          { name: 'difficulty', label: 'Difficulty', type: 'select', options: 'difficulty', placeholder: 'Select difficulty' },
          { name: 'language', label: 'Language', type: 'select', options: 'languages', placeholder: 'Select Language (default: English)' }
        ],
        endpoint: '/v1/admin/flashcard-decks/{deck_id}/cards'
      }
    ]

    // Filtered options for cascading selects
    const filteredSubjects = ref([])
    const filteredTopics = ref([])
    const filteredLessons = ref([])

    // Configure marked
    marked.setOptions({ breaks: true, gfm: true })

    // Load/Save State
    const loadState = () => {
      try {
        const saved = localStorage.getItem(STORAGE_KEY)
        if (saved) {
          const state = JSON.parse(saved)
          messages.value = (state.messages || []).map(m => ({ ...m, isNew: false }))
          sessionId.value = state.sessionId || null
          isExpanded.value = state.isExpanded || false
        }
      } catch (e) { console.error('Failed to load chat state:', e) }
    }

    const saveState = () => {
      try {
        const state = {
          messages: messages.value.map(m => ({ ...m, isNew: false, sending: false })),
          sessionId: sessionId.value,
          isExpanded: isExpanded.value
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
      } catch (e) { console.error('Failed to save chat state:', e) }
    }

    watch([messages, sessionId, isExpanded], saveState, { deep: true })
    
    // Watch for mode changes to load data only when needed
    watch(activeMode, async (newMode) => {
      if (newMode === 'generate' && exams.value.length === 0) {
        await loadExams()
      }
      if (newMode === 'create' && exams.value.length === 0) {
        await loadExams()
      }
    })

    // Watch for chat open to potentially load data
    watch(isOpen, async (opened) => {
      if (opened && activeMode.value === 'generate' && exams.value.length === 0) {
        await loadExams()
      }
    })
    
    onMounted(() => {
      loadState()
      if (messages.value.length) nextTick(scrollToBottom)
      // Don't load exams here - load them lazily when needed
    })

    // ==================== API LOADERS ====================
    const loadExams = async () => {
      if (exams.value.length > 0) return // Already loaded
      try {
        const res = await api.get('/v1/admin/exams')
        exams.value = res.data.exams || res.data || []
      } catch (e) { console.error('Failed to load exams:', e) }
    }

    const loadSubjects = async () => {
      if (!selectedExam.value) { subjects.value = []; topics.value = []; lessons.value = []; return }
      try {
        const res = await api.get(`/v1/admin/exams/${selectedExam.value}/subjects`)
        subjects.value = res.data.subjects || res.data || []
        selectedSubject.value = ''
        selectedTopic.value = ''
        selectedLesson.value = ''
        topics.value = []
        lessons.value = []
      } catch (e) { console.error('Failed to load subjects:', e) }
    }

    const loadTopics = async () => {
      if (!selectedSubject.value) { topics.value = []; lessons.value = []; selectedTopic.value = ''; selectedLesson.value = ''; return }
      try {
        const res = await api.get(`/v1/admin/subjects/${selectedSubject.value}/topics`)
        topics.value = res.data.topics || res.data || []
        selectedTopic.value = ''
        selectedLesson.value = ''
        lessons.value = []
      } catch (e) { console.error('Failed to load topics:', e) }
    }

    const loadLessons = async () => {
      if (!selectedTopic.value) { lessons.value = []; selectedLesson.value = ''; return }
      try {
        const res = await api.get(`/v1/admin/topics/${selectedTopic.value}/lessons`)
        lessons.value = res.data.lessons || res.data || []
        selectedLesson.value = ''
      } catch (e) { console.error('Failed to load lessons:', e) }
    }

    const loadAllSubjects = async () => {
      try {
        const res = await api.get('/v1/admin/subjects')
        allSubjects.value = res.data.subjects || res.data || []
      } catch (e) { allSubjects.value = [] }
    }

    const loadAllTopics = async () => {
      try {
        const res = await api.get('/v1/admin/topics')
        allTopics.value = res.data.topics || res.data || []
      } catch (e) { allTopics.value = [] }
    }

    const loadAllLessons = async () => {
      try {
        const res = await api.get('/v1/admin/lessons')
        allLessons.value = res.data.lessons || res.data || []
      } catch (e) { allLessons.value = [] }
    }

    const loadAllTests = async () => {
      try {
        const res = await api.get('/v1/admin/tests')
        allTests.value = res.data.tests || res.data || []
      } catch (e) { allTests.value = [] }
    }

    const loadAllFlashcardDecks = async () => {
      try {
        const res = await api.get('/v1/admin/flashcard-decks')
        const payload = res.data.data || res.data
        allFlashcardDecks.value = payload.decks || payload || []
      } catch (e) { allFlashcardDecks.value = [] }
    }

    // Load subjects for a specific exam (for cascading selects)
    const loadSubjectsForExam = async (examId) => {
      if (!examId) { filteredSubjects.value = []; return }
      try {
        const res = await api.get(`/v1/admin/exams/${examId}/subjects`)
        filteredSubjects.value = res.data.subjects || res.data || []
      } catch (e) { filteredSubjects.value = [] }
    }

    const loadTopicsForSubject = async (subjectId) => {
      if (!subjectId) { filteredTopics.value = []; return }
      try {
        const res = await api.get(`/v1/admin/subjects/${subjectId}/topics`)
        filteredTopics.value = res.data.topics || res.data || []
      } catch (e) { filteredTopics.value = [] }
    }

    const loadLessonsForTopic = async (topicId) => {
      if (!topicId) { filteredLessons.value = []; return }
      try {
        const res = await api.get(`/v1/admin/topics/${topicId}/lessons`)
        filteredLessons.value = res.data.lessons || res.data || []
      } catch (e) { filteredLessons.value = [] }
    }

    // ==================== MARKDOWN/MATH RENDERING ====================
    const renderMath = (text) => {
      if (!text) return text
      text = text.replace(/\$\$([\s\S]*?)\$\$/g, (match, math) => {
        try { return katex.renderToString(math.trim(), { displayMode: true, throwOnError: false }) }
        catch (e) { return match }
      })
      text = text.replace(/\$([^$\n]+?)\$/g, (match, math) => {
        try { return katex.renderToString(math.trim(), { displayMode: false, throwOnError: false }) }
        catch (e) { return match }
      })
      return text
    }

    const renderMarkdown = (content) => content ? marked.parse(renderMath(content)) : ''
    const escapeHtml = (text) => {
      if (!text) return ''
      const div = document.createElement('div')
      div.textContent = text
      return div.innerHTML.replace(/\n/g, '<br>')
    }

    // ==================== UI TOGGLES ====================
    const toggleChat = () => {
      isOpen.value = !isOpen.value
      if (isOpen.value) nextTick(() => { inputField.value?.focus(); scrollToBottom() })
    }
    const toggleExpanded = () => { isExpanded.value = !isExpanded.value }
    const toggleMode = () => { activeMode.value = activeMode.value === 'chat' ? 'create' : 'chat' }
    const triggerFileInput = () => { fileInput.value?.click() }
    const handleFileSelect = (e) => { attachments.value.push(...Array.from(e.target.files || [])); e.target.value = '' }
    const removeAttachment = (index) => { attachments.value.splice(index, 1) }

    // ==================== CONTENT PARSING ====================
    const parseQuestionsFromText = (text) => {
      const questions = []
      
      console.log('parseQuestionsFromText called, text length:', text?.length)
      
      // Try multiple splitting strategies
      // Strategy 1: Split by "Q" or "Question" followed by number
      let questionBlocks = text.split(/(?=(?:^|\n)\s*(?:Q(?:uestion)?\.?\s*)?\d+[\.\)\:])/i)
      
      // Strategy 2: If that didn't work well, try splitting by double newlines
      if (questionBlocks.length <= 1) {
        questionBlocks = text.split(/\n\n+/)
      }
      
      console.log('Found', questionBlocks.length, 'potential question blocks')
      
      for (let i = 0; i < questionBlocks.length; i++) {
        const block = questionBlocks[i]?.trim()
        if (!block || block.length < 20) continue
        
        const question = { question_type: 'mcq', marks: 1, difficulty: 'medium' }
        
        // Try to extract question text (everything before options)
        let qText = ''
        const optionStart = block.search(/\n\s*[Aa][\.\)\:]/)
        if (optionStart > 0) {
          qText = block.substring(0, optionStart).replace(/^(?:Q(?:uestion)?\.?\s*)?\d+[\.\)\:]\s*/i, '').trim()
        } else {
          // Try another pattern - look for the first line as question
          const lines = block.split('\n')
          const firstLine = lines[0]?.replace(/^(?:Q(?:uestion)?\.?\s*)?\d*[\.\)\:]?\s*/i, '').trim()
          if (firstLine && firstLine.length > 10) {
            qText = firstLine
          }
        }
        
        if (!qText || qText.length < 5) continue
        question.question_text = qText
        
        // Extract options - multiple patterns
        const options = {}
        
        // Pattern 1: A. or A) or a. or a) or A:
        const optPatterns = [
          /[Aa][\.\)\:]\s*([^\n]+)/,
          /[Bb][\.\)\:]\s*([^\n]+)/,
          /[Cc][\.\)\:]\s*([^\n]+)/,
          /[Dd][\.\)\:]\s*([^\n]+)/
        ]
        
        const optA = block.match(/[Aa][\.\)\:]\s*([^\n]+)/)
        const optB = block.match(/[Bb][\.\)\:]\s*([^\n]+)/)
        const optC = block.match(/[Cc][\.\)\:]\s*([^\n]+)/)
        const optD = block.match(/[Dd][\.\)\:]\s*([^\n]+)/)
        
        if (optA) options.a = optA[1].trim()
        if (optB) options.b = optB[1].trim()
        if (optC) options.c = optC[1].trim()
        if (optD) options.d = optD[1].trim()
        
        // If we have at least 2 options, use them
        if (Object.keys(options).length >= 2) {
          question.options = options
        } else {
          // Try to find options in a different format (numbered list under question)
          const numberedOpts = [...block.matchAll(/(?:^|\n)\s*(\d)[\.\)]\s*([^\n]+)/g)]
          if (numberedOpts.length >= 2) {
            const optKeys = ['a', 'b', 'c', 'd']
            numberedOpts.slice(0, 4).forEach((m, idx) => {
              options[optKeys[idx]] = m[2].trim()
            })
            question.options = options
          } else {
            continue // Skip if no valid options
          }
        }
        
        // Extract correct answer
        const answerPatterns = [
          /(?:correct\s*answer|answer)\s*[:\-]?\s*([A-Da-d])/i,
          /\*\*([A-Da-d])\*\*/,
          /([A-Da-d])\s*(?:is\s*)?(?:the\s*)?correct/i,
          /correct[:\s]+([A-Da-d])/i
        ]
        for (const pattern of answerPatterns) {
          const match = block.match(pattern)
          if (match) {
            question.correct_answer = match[1].toLowerCase()
            break
          }
        }
        
        // If no answer found, default to 'a'
        if (!question.correct_answer) {
          question.correct_answer = 'a'
        }
        
        // Extract explanation
        const explMatch = block.match(/(?:explanation|reason|why)[:\s]*([\s\S]*?)(?=\n\n|\n(?:Q|Question|\d+[\.\)])|$)/i)
        if (explMatch) question.explanation = explMatch[1].trim().substring(0, 500)
        
        // Extract difficulty
        const diffMatch = block.match(/(?:difficulty|level)[:\s]*(easy|medium|hard|simple|difficult)/i)
        if (diffMatch) {
          const d = diffMatch[1].toLowerCase()
          question.difficulty = d === 'simple' ? 'easy' : d === 'difficult' ? 'hard' : d
        }
        
        questions.push(question)
      }
      
      console.log('parseQuestionsFromText returning', questions.length, 'questions')
      return questions
    }

    const parseSubjectsFromText = (text) => {
      const subjects = []
      const lines = text.split('\n')
      
      console.log('parseSubjectsFromText called, lines:', lines.length)
      
      for (const line of lines) {
        const trimmed = line.trim()
        if (!trimmed || trimmed.length < 3) continue
        
        // Skip header-like lines
        if (trimmed.match(/^(subjects?|list|here|following|generated)/i)) continue
        
        // Match numbered items, bullet points, or bold text
        const patterns = [
          /^(?:\d+[\.\)]|\*|\-|•)\s*\*?\*?(.+?)\*?\*?(?:\s*[-–:]\s*(.+))?$/,
          /^\*\*(.+?)\*\*(?:\s*[-–:]\s*(.+))?$/,
          /^#{1,3}\s*(.+?)(?:\s*[-–:]\s*(.+))?$/,
          /^(.+?)(?:\s*[-–:]\s*(.+))?$/  // Fallback: any line with optional description
        ]
        
        for (const pattern of patterns) {
          const match = trimmed.match(pattern)
          if (match) {
            let name = match[1].replace(/\*\*/g, '').replace(/^[\d\.\)\-\*•]+\s*/, '').trim()
            const description = match[2]?.trim() || ''
            
            // Validate name
            if (name.length > 2 && name.length < 100 && 
                !name.match(/^(subject|topic|lesson|note|here|following|list)/i) &&
                !name.match(/^[\d\.\)\-\*•]+$/)) {
              
              // Try to extract importance from description or name
              let importance = 'medium'
              const fullText = (name + ' ' + description).toLowerCase()
              if (fullText.match(/high|important|critical|essential|must/i)) importance = 'high'
              if (fullText.match(/low|optional|basic|supplementary/i)) importance = 'low'
              
              // Try to extract hours
              const hoursMatch = (name + ' ' + description).match(/(\d+)\s*(?:hours?|hrs?)/i)
              const estimated_hours = hoursMatch ? parseInt(hoursMatch[1]) : null
              
              subjects.push({ 
                name, 
                description, 
                status: 'draft', 
                importance,
                ...(estimated_hours && { estimated_hours })
              })
              break
            }
          }
        }
      }
      
      console.log('parseSubjectsFromText returning', subjects.length, 'subjects')
      return subjects
    }

    const parseTopicsFromText = (text) => {
      const topics = []
      const lines = text.split('\n')
      
      console.log('parseTopicsFromText called, lines:', lines.length)
      
      for (const line of lines) {
        const trimmed = line.trim()
        if (!trimmed || trimmed.length < 3) continue
        
        // Skip header-like lines
        if (trimmed.match(/^(topics?|list|here|following|generated|syllabus)/i)) continue
        
        // Match various list formats
        const patterns = [
          /^(?:\d+[\.\)]|\*|\-|•)\s*\*?\*?(.+?)\*?\*?(?:\s*[-–:]\s*(.+))?$/,
          /^\*\*(.+?)\*\*(?:\s*[-–:]\s*(.+))?$/,
          /^#{1,4}\s*(.+?)(?:\s*[-–:]\s*(.+))?$/,
          /^(.+?)(?:\s*[-–:]\s*(.+))?$/  // Fallback
        ]
        
        for (const pattern of patterns) {
          const match = trimmed.match(pattern)
          if (match) {
            let name = match[1].replace(/\*\*/g, '').replace(/^[\d\.\)\-\*•]+\s*/, '').trim()
            const description = match[2]?.trim() || ''
            
            if (name.length > 2 && name.length < 150 && 
                !name.match(/^(topic|syllabus|overview|introduction|here|following|list)/i) &&
                !name.match(/^[\d\.\)\-\*•]+$/)) {
              
              // Try to extract importance from description
              let importance = 'medium'
              const fullText = (name + ' ' + description).toLowerCase()
              if (fullText.match(/high|important|critical|essential|must/i)) importance = 'high'
              if (fullText.match(/low|optional|basic|supplementary/i)) importance = 'low'
              
              // Try to extract hours
              const hoursMatch = fullText.match(/(\d+)\s*(?:hours?|hrs?)/i)
              const estimated_hours = hoursMatch ? parseInt(hoursMatch[1]) : null
              
              topics.push({ 
                name, 
                description, 
                status: 'draft', 
                importance,
                ...(estimated_hours && { estimated_hours })
              })
              break
            }
          }
        }
      }
      
      console.log('parseTopicsFromText returning', topics.length, 'topics')
      return topics
    }

    const parseLessonsFromText = (text) => {
      const lessons = []
      
      console.log('parseLessonsFromText called, text length:', text?.length)
      
      // Try to split by lesson markers
      const sections = text.split(/(?=(?:^|\n)(?:#{1,3}\s*)?(?:Lesson\s*\d*[:\.\-]?|Unit\s*\d*[:\.\-]?|\d+[\.\)]\s+))/i)
      
      for (const section of sections) {
        if (!section.trim()) continue
        
        // Extract title
        const titlePatterns = [
          /^(?:#{1,3}\s*)?(?:Lesson\s*\d*[:\.\-]?\s*)?(.+?)(?:\n|$)/i,
          /^\d+[\.\)]\s*(.+?)(?:\n|$)/,
          /^\*\*(.+?)\*\*(?:\n|$)/
        ]
        
        let title = ''
        let content = section
        
        for (const pattern of titlePatterns) {
          const match = section.match(pattern)
          if (match && match[1].trim().length > 3) {
            title = match[1].replace(/\*\*/g, '').trim()
            content = section.replace(match[0], '').trim()
            break
          }
        }
        
        if (title && title.length > 3 && title.length < 200) {
          // Try to extract duration
          const durationMatch = content.match(/(?:duration|time)[:\s]*(\d+)\s*(?:min|minute|hour)?/i)
          const duration = durationMatch ? parseInt(durationMatch[1]) : 45
          
          // Try to extract objectives
          const objMatch = content.match(/(?:objectives?|goals?)[:\s]*([\s\S]*?)(?=\n\n|\n(?:#{1,3}|Content|Overview)|$)/i)
          const objectives = objMatch ? objMatch[1].split(/\n/).filter(l => l.trim()).slice(0, 5) : []
          
          lessons.push({ 
            title, 
            content: content.substring(0, 1000), 
            status: 'draft',
            duration_minutes: duration,
            learning_objectives: objectives.map(o => o.replace(/^[\-\*•]\s*/, '').trim()).filter(o => o.length > 0)
          })
        }
      }
      
      // If no lessons found, try line-by-line parsing
      if (lessons.length === 0) {
        const lines = text.split('\n')
        for (const line of lines) {
          const trimmed = line.trim()
          if (!trimmed || trimmed.length < 5) continue
          
          // Skip header-like lines
          if (trimmed.match(/^(lessons?|list|here|following|generated)/i)) continue
          
          const patterns = [
            /^(?:\d+[\.\)]|\*|\-|•)\s*\*?\*?(.+?)\*?\*?(?:\s*[-–:]\s*(.+))?$/,
            /^\*\*(.+?)\*\*(?:\s*[-–:]\s*(.+))?$/
          ]
          
          for (const pattern of patterns) {
            const match = trimmed.match(pattern)
            if (match) {
              const title = match[1].replace(/\*\*/g, '').replace(/^[\d\.\)\-\*•]+\s*/, '').trim()
              const description = match[2]?.trim() || ''
              
              if (title.length > 3 && title.length < 200 && !title.match(/^(lesson|here|following|list)/i)) {
                lessons.push({
                  title,
                  content: description,
                  status: 'draft',
                  duration_minutes: 45
                })
                break
              }
            }
          }
        }
      }
      
      // If still no lessons found, create one from the whole text
      if (lessons.length === 0 && text.trim().length > 50) {
        const firstLine = text.split('\n')[0]?.trim() || 'Generated Lesson'
        lessons.push({ 
          title: firstLine.substring(0, 100), 
          content: text, 
          status: 'draft',
          duration_minutes: 45
        })
      }
      
      console.log('parseLessonsFromText returning', lessons.length, 'lessons')
      return lessons
    }

    const parseFlashcardsFromText = (text) => {
      const flashcards = []
      
      console.log('parseFlashcardsFromText called, text length:', text?.length)
      
      // Pattern 1: Front: ... Back: ... or Q: ... A: ...
      const qaPatterns = [
        /(?:Front|Question|Q)[:\s]*([^\n]+)\s*\n\s*(?:Back|Answer|A)[:\s]*([^\n]+)/gi,
        /(?:Term|Word|Concept)[:\s]*([^\n]+)\s*\n\s*(?:Definition|Meaning|Explanation)[:\s]*([^\n]+)/gi
      ]
      
      for (const pattern of qaPatterns) {
        const matches = [...text.matchAll(pattern)]
        for (const match of matches) {
          const front = match[1]?.trim()
          const back = match[2]?.trim()
          if (front && back && front.length > 2 && back.length > 2) {
            flashcards.push({ front, back, type: 'qa' })
          }
        }
        if (flashcards.length > 0) {
          console.log('parseFlashcardsFromText returning', flashcards.length, 'flashcards (pattern 1)')
          return flashcards
        }
      }
      
      // Pattern 2: Numbered list with answers
      const numberedMatches = [...text.matchAll(/(\d+)[\.\)]\s*([^\n]+)\s*\n\s*(?:[-•]\s*)?(?:Answer|A)?[:\s]*([^\n]+)/gi)]
      for (const match of numberedMatches) {
        const front = match[2]?.trim()
        const back = match[3]?.trim()
        if (front && back && front.length > 2) {
          flashcards.push({ front, back, type: 'qa' })
        }
      }
      if (flashcards.length > 0) {
        console.log('parseFlashcardsFromText returning', flashcards.length, 'flashcards (pattern 2)')
        return flashcards
      }
      
      // Pattern 3: Bold term followed by definition
      const boldMatches = [...text.matchAll(/\*\*([^*]+)\*\*[:\s]*([^\n]+)/g)]
      for (const match of boldMatches) {
        const front = match[1]?.trim()
        const back = match[2]?.trim()
        if (front && back && front.length > 2) {
          flashcards.push({ front, back, type: 'definition' })
        }
      }
      if (flashcards.length > 0) {
        console.log('parseFlashcardsFromText returning', flashcards.length, 'flashcards (pattern 3)')
        return flashcards
      }
      
      // Pattern 4: Numbered items with colon separator (1. Term: Definition)
      const colonMatches = [...text.matchAll(/(?:^|\n)\s*\d+[\.\)]\s*([^:\n]+):\s*([^\n]+)/g)]
      for (const match of colonMatches) {
        const front = match[1]?.trim()
        const back = match[2]?.trim()
        if (front && back && front.length > 2 && back.length > 2) {
          flashcards.push({ front, back, type: 'definition' })
        }
      }
      if (flashcards.length > 0) {
        console.log('parseFlashcardsFromText returning', flashcards.length, 'flashcards (pattern 4)')
        return flashcards
      }
      
      // Pattern 5: Split by double newlines (pairs)
      const blocks = text.split(/\n\n+/).filter(b => b.trim())
      for (let i = 0; i < blocks.length - 1; i += 2) {
        const front = blocks[i]?.trim().replace(/^\d+[\.\)]\s*/, '')
        const back = blocks[i + 1]?.trim()
        if (front && back && front.length > 2 && front.length < 200) {
          flashcards.push({ front, back, type: 'qa' })
        }
      }
      
      console.log('parseFlashcardsFromText returning', flashcards.length, 'flashcards (pattern 5 or none)')
      return flashcards
    }

    const parseNotesFromText = (text) => {
      return [{ title: 'Generated Notes', content: text, status: 'draft' }]
    }

    // Try to parse JSON from response
    const tryParseJSON = (text) => {
      try {
        // Find JSON in the text
        const jsonMatch = text.match(/```json\s*([\s\S]*?)```/i) || text.match(/\{[\s\S]*\}/)
        if (jsonMatch) {
          const jsonStr = jsonMatch[1] || jsonMatch[0]
          return JSON.parse(jsonStr)
        }
      } catch (e) { /* ignore */ }
      return null
    }

    // Detect content type from response
    const detectContentType = (text, userQuery) => {
      const lowerText = text.toLowerCase()
      const lowerQuery = userQuery.toLowerCase()
      
      if (lowerQuery.includes('question') || lowerQuery.includes('mcq') || 
          lowerText.includes('correct answer') || /[A-D]\)\s/.test(text)) {
        return 'questions'
      }
      if (lowerQuery.includes('flashcard') || lowerText.includes('front:') || lowerText.includes('back:')) {
        return 'flashcards'
      }
      if (lowerQuery.includes('subject') && (lowerQuery.includes('generate') || lowerQuery.includes('list'))) {
        return 'subjects'
      }
      if (lowerQuery.includes('topic') && (lowerQuery.includes('generate') || lowerQuery.includes('list'))) {
        return 'topics'
      }
      if (lowerQuery.includes('lesson') && (lowerQuery.includes('generate') || lowerQuery.includes('create'))) {
        return 'lessons'
      }
      if (lowerQuery.includes('note') || lowerQuery.includes('summary')) {
        return 'notes'
      }
      if (lowerQuery.includes('syllabus')) {
        return 'topics' // Syllabus generates topics
      }
      return null
    }

    // Parse content based on type
    const parseContent = (text, contentType) => {
      // First try JSON parsing
      const jsonData = tryParseJSON(text)
      if (jsonData) {
        if (jsonData.questions) return jsonData.questions
        if (jsonData.subjects) return jsonData.subjects
        if (jsonData.topics) return jsonData.topics
        if (jsonData.lessons) return jsonData.lessons
        if (jsonData.flashcards) return jsonData.flashcards
        if (Array.isArray(jsonData)) return jsonData
      }
      
      // Fallback to text parsing
      switch (contentType) {
        case 'questions': return parseQuestionsFromText(text)
        case 'subjects': return parseSubjectsFromText(text)
        case 'topics': return parseTopicsFromText(text)
        case 'lessons': return parseLessonsFromText(text)
        case 'flashcards': return parseFlashcardsFromText(text)
        case 'notes': return parseNotesFromText(text)
        default: return []
      }
    }

    // Parse AI response for actionable buttons
    const parseActionsFromResponse = (response, userQuery) => {
      const actions = []
      const contentType = detectContentType(response, userQuery)
      
      if (!contentType) return null
      
      const parsed = parseContent(response, contentType)
      if (!parsed || parsed.length === 0) return null
      
      const config = CONTENT_TYPES[contentType]
      if (!config) return null
      
      // Add primary action
      actions.push({
        label: `Add ${parsed.length} ${contentType}`,
        icon: 'plus',
        action: 'saveContent',
        contentType,
        data: response,
        parsedData: parsed,
        variant: 'primary'
      })
      
      // Add secondary actions based on content type
      if (contentType === 'questions') {
        actions.push({
          label: 'Create Test with These',
          icon: 'clipboard',
          action: 'createTestWithQuestions',
          data: response,
          parsedData: parsed,
          variant: 'secondary'
        })
      }
      
      if (contentType === 'topics') {
        actions.push({
          label: 'Generate Lessons for Each',
          icon: 'file-text',
          action: 'generateLessonsForTopics',
          data: response,
          parsedData: parsed,
          variant: 'secondary'
        })
      }
      
      if (contentType === 'subjects') {
        actions.push({
          label: 'Generate Topics for Each',
          icon: 'hash',
          action: 'generateTopicsForSubjects',
          data: response,
          parsedData: parsed,
          variant: 'secondary'
        })
      }
      
      return actions.length > 0 ? actions : null
    }

    // ==================== CHAT FUNCTIONS ====================
    const sendMessage = async () => {
      const content = inputMessage.value.trim()
      if (!content && !attachments.value.length) return
      if (loading.value) return

      const msgId = Date.now()
      const userMessage = {
        id: msgId, role: 'user', content: content || '(Attached files)',
        timestamp: new Date().toISOString(), isNew: true, sending: true,
        attachments: attachments.value.map(f => ({ name: f.name, type: f.type, size: f.size }))
      }
      messages.value.push(userMessage)
      const filesToSend = [...attachments.value]
      const userQuery = content
      inputMessage.value = ''
      attachments.value = []
      scrollToBottom()

      setTimeout(() => { userMessage.sending = false }, 500)

      loading.value = true
      loadingText.value = 'Thinking'
      
      try {
        let response
        if (filesToSend.length) {
          const formData = new FormData()
          formData.append('message', content)
          if (sessionId.value) formData.append('session_id', sessionId.value)
          filesToSend.forEach(file => formData.append('files', file))
          response = await api.post('/v1/admin/admin-chat', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 })
        } else {
          response = await api.post('/v1/admin/admin-chat', { message: content, session_id: sessionId.value }, { timeout: 120000 })
        }
        
        if (response.data.session_id) sessionId.value = response.data.session_id
        
        const aiResponse = response.data.response
        const actions = parseActionsFromResponse(aiResponse, userQuery)
        
        messages.value.push({ 
          id: Date.now(), 
          role: 'assistant', 
          content: aiResponse, 
          timestamp: new Date().toISOString(), 
          isNew: true,
          actions,
          userQuery
        })
      } catch (error) {
        const errorMsg = error.response?.data?.error?.message || error.message || 'Failed to get response.'
        messages.value.push({ 
          id: Date.now(), 
          role: 'assistant', 
          content: `❌ Error: ${errorMsg}`, 
          timestamp: new Date().toISOString(), 
          isError: true, 
          isNew: true 
        })
      } finally {
        loading.value = false
        scrollToBottom()
        setTimeout(() => { messages.value.forEach(m => m.isNew = false) }, 1000)
      }
    }

    // Execute action from AI response
    const executeAction = async (action, message) => {
      currentMessageData.value = message
      
      switch (action.action) {
        case 'saveContent':
          openSaveModal(action.contentType, action.parsedData, action.data)
          break
          
        case 'createTestWithQuestions':
          // Open create test modal with questions pre-loaded
          const testCard = createCards.find(c => c.type === 'test')
          if (testCard) {
            await openCreateModal(testCard)
            // Store questions to add after test creation
            createForm._pendingQuestions = action.parsedData
          }
          break
          
        case 'generateLessonsForTopics':
          // Generate lessons for each topic
          for (const topic of action.parsedData.slice(0, 3)) {
            const prompt = `Generate a detailed lesson for the topic "${topic.name}". Include introduction, main content, examples, and summary.`
            inputMessage.value = prompt
            await sendMessage()
          }
          break
          
        case 'generateTopicsForSubjects':
          // Generate topics for each subject
          for (const subject of action.parsedData.slice(0, 3)) {
            const prompt = `Generate a list of 5-10 topics for the subject "${subject.name}". Include topic names and brief descriptions.`
            inputMessage.value = prompt
            await sendMessage()
          }
          break
          
        default:
          addSystemMessage(`Action "${action.label}" is not yet implemented.`)
      }
    }

    const addSystemMessage = (content) => {
      messages.value.push({
        id: Date.now(),
        role: 'assistant',
        content,
        timestamp: new Date().toISOString(),
        isNew: true
      })
      scrollToBottom()
    }

    const sendQuickAction = (prompt) => { 
      inputMessage.value = prompt
      inputField.value?.focus() 
    }

    const clearChat = async () => {
      if (sessionId.value) { 
        try { await api.delete(`/v1/admin/admin-chat/${sessionId.value}`) } 
        catch { /* ignore */ } 
      }
      messages.value = []
      sessionId.value = null
      attachments.value = []
      localStorage.removeItem(STORAGE_KEY)
    }

    const copyMessage = async (content) => {
      try {
        await navigator.clipboard.writeText(content)
        // Could add toast notification here
      } catch (e) {
        console.error('Failed to copy:', e)
      }
    }
    
    const formatTime = (date) => new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    const scrollToBottom = () => { nextTick(() => { if (chatContainer.value) chatContainer.value.scrollTop = chatContainer.value.scrollHeight }) }
    const autoResize = (e) => { const t = e.target; t.style.height = 'auto'; t.style.height = Math.min(t.scrollHeight, 100) + 'px' }

    // ==================== SAVE MODAL FUNCTIONS ====================
    const openSaveModal = (contentType, parsed, rawData) => {
      const config = CONTENT_TYPES[contentType]
      if (!config) return
      
      saveModalConfig.value = { ...config, contentType }
      parsedContent.value = parsed
      selectedItems.value = parsed.map((_, i) => i) // Select all by default
      previewLimit.value = 5
      
      // Only pre-fill if not already set (openSaveModalFromGenerated pre-populates these)
      if (!saveTarget.examId && selectedExam.value) saveTarget.examId = selectedExam.value
      if (!saveTarget.subjectId && selectedSubject.value) saveTarget.subjectId = selectedSubject.value
      if (!saveTarget.topicId && selectedTopic.value) saveTarget.topicId = selectedTopic.value
      if (!saveTarget.lessonId && selectedLesson.value) saveTarget.lessonId = selectedLesson.value
      
      // Only load dependent data if not already loaded (openSaveModalFromGenerated handles this)
      if (saveTarget.examId && saveSubjects.value.length === 0) {
        onSaveExamChange()
      }
      
      showSaveModal.value = true
    }

    const closeSaveModal = () => {
      showSaveModal.value = false
      saveModalConfig.value = {}
      parsedContent.value = []
      selectedItems.value = []
      Object.keys(saveTarget).forEach(k => saveTarget[k] = '')
    }

    const onSaveExamChange = async () => {
      // Store current values to preserve if still valid
      const currentSubjectId = saveTarget.subjectId
      const currentTopicId = saveTarget.topicId
      const currentLessonId = saveTarget.lessonId
      
      saveTarget.subjectId = ''
      saveTarget.topicId = ''
      saveTarget.lessonId = ''
      saveTarget.testId = ''
      saveTopics.value = []
      saveLessons.value = []
      saveTests.value = []
      
      if (saveTarget.examId) {
        try {
          const res = await api.get(`/v1/admin/exams/${saveTarget.examId}/subjects`)
          saveSubjects.value = res.data.subjects || res.data || []
          
          // Restore subject if it belongs to this exam
          if (currentSubjectId && saveSubjects.value.some(s => s.id === currentSubjectId)) {
            saveTarget.subjectId = currentSubjectId
            // Load topics for this subject
            await onSaveSubjectChange()
            
            // Restore topic if it belongs to this subject
            if (currentTopicId && saveTopics.value.some(t => t.id === currentTopicId)) {
              saveTarget.topicId = currentTopicId
              // Load lessons for this topic
              await onSaveTopicChange()
              
              // Restore lesson if it belongs to this topic
              if (currentLessonId && saveLessons.value.some(l => l.id === currentLessonId)) {
                saveTarget.lessonId = currentLessonId
              }
            }
          }
        } catch (e) { saveSubjects.value = [] }
      } else {
        saveSubjects.value = []
      }
    }

    const onSaveSubjectChange = async () => {
      const currentTopicId = saveTarget.topicId
      const currentLessonId = saveTarget.lessonId
      
      saveTarget.topicId = ''
      saveTarget.lessonId = ''
      saveTarget.testId = ''
      saveLessons.value = []
      saveTests.value = []
      
      if (saveTarget.subjectId) {
        try {
          const res = await api.get(`/v1/admin/subjects/${saveTarget.subjectId}/topics`)
          saveTopics.value = res.data.topics || res.data || []
          
          // Restore topic if it belongs to this subject
          if (currentTopicId && saveTopics.value.some(t => t.id === currentTopicId)) {
            saveTarget.topicId = currentTopicId
            await onSaveTopicChange()
            
            // Restore lesson if it belongs to this topic
            if (currentLessonId && saveLessons.value.some(l => l.id === currentLessonId)) {
              saveTarget.lessonId = currentLessonId
            }
          }
        } catch (e) { saveTopics.value = [] }
      } else {
        saveTopics.value = []
      }
    }

    const onSaveTopicChange = async () => {
      const currentLessonId = saveTarget.lessonId
      
      saveTarget.lessonId = ''
      saveTarget.testId = ''
      saveTests.value = []
      
      if (saveTarget.topicId) {
        try {
          const [lessonsRes, testsRes] = await Promise.all([
            api.get(`/v1/admin/topics/${saveTarget.topicId}/lessons`),
            api.get(`/v1/admin/topics/${saveTarget.topicId}/tests`).catch(() => api.get('/v1/admin/tests'))
          ])
          saveLessons.value = lessonsRes.data.lessons || lessonsRes.data || []
          
          // Filter tests by topic if we got all tests
          const allTests = testsRes.data.tests || testsRes.data || []
          saveTests.value = allTests.filter(t => 
            t.topic_id === saveTarget.topicId || !t.topic_id
          )
          
          // Restore lesson if it belongs to this topic
          if (currentLessonId && saveLessons.value.some(l => l.id === currentLessonId)) {
            saveTarget.lessonId = currentLessonId
          }
        } catch (e) { 
          saveLessons.value = []
          saveTests.value = []
        }
      } else {
        saveLessons.value = []
      }
    }

    const canSaveContent = computed(() => {
      const config = saveModalConfig.value
      if (!config.contentType) return false
      
      // For notes/content, we don't need selected items - just the raw content
      const isNotesOrContent = config.contentType === 'notes' || config.contentType === 'content'
      if (!isNotesOrContent && selectedItems.value.length === 0) return false
      
      if (config.requiresExam && !saveTarget.examId) return false
      if (config.requiresSubject && !saveTarget.subjectId) return false
      if (config.requiresTopic && !saveTarget.topicId) return false
      
      // For questions: test is optional - we'll auto-create if needed
      // Only require test if explicitly set in config AND there's no topic to create test under
      if (config.requiresTest && !saveTarget.testId && !saveTarget.topicId) return false
      
      return true
    })

    const selectAllItems = () => {
      if (selectedItems.value.length === parsedContent.value.length) {
        selectedItems.value = []
      } else {
        selectedItems.value = parsedContent.value.map((_, i) => i)
      }
    }

    const getPreviewTitle = (item, index) => {
      if (item.question_text) return `Q${index + 1}: ${truncateText(item.question_text, 60)}`
      if (item.name) return item.name
      if (item.title) return item.title
      if (item.front) return `Card ${index + 1}: ${truncateText(item.front, 50)}`
      return `Item ${index + 1}`
    }

    const truncateText = (text, maxLength) => {
      if (!text) return ''
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
    }

    const saveContent = async () => {
      if (!canSaveContent.value) return
      
      savingContent.value = true
      const contentType = saveModalConfig.value.contentType
      const itemsToSave = selectedItems.value.map(i => parsedContent.value[i])
      
      let successCount = 0
      let errorCount = 0
      let testId = saveTarget.testId
      
      try {
        // For questions: Auto-create test if none selected and topic is available
        if (contentType === 'questions' && !testId && saveTarget.topicId) {
          try {
            // Get context names for test title
            const examName = exams.value.find(e => e.id === saveTarget.examId)?.name || 'Exam'
            const subjectName = saveSubjects.value.find(s => s.id === saveTarget.subjectId)?.name || 'Subject'
            const topicName = saveTopics.value.find(t => t.id === saveTarget.topicId)?.name || 'Topic'
            const lessonName = saveLessons.value.find(l => l.id === saveTarget.lessonId)?.title || ''
            
            // Create test title based on context
            const testTitle = lessonName 
              ? `${lessonName} - AI Generated Test`
              : `${topicName} - AI Generated Test`
            
            const testPayload = {
              title: testTitle,
              description: `Auto-generated test for ${topicName}${lessonName ? ` - ${lessonName}` : ''}`,
              topic_id: saveTarget.topicId,
              lesson_id: saveTarget.lessonId || null,
              duration_minutes: generatedTestMeta.value?.duration || 30,
              total_marks: itemsToSave.length,
              passing_marks: Math.ceil(itemsToSave.length * 0.4),
              status: 'draft',
              test_type: 'practice'
            }
            
            const testRes = await api.post('/v1/admin/tests', testPayload)
            testId = testRes.data.test?.id || testRes.data.id
            
            if (testId) {
              toast.info(`Created new test: "${testTitle}"`)
            }
          } catch (e) {
            console.error('Failed to auto-create test:', e)
            toast.warning(`Could not auto-create test. Questions will be saved without a test.`)
          }
        }
        
        // Handle notes/content as single item save
        if ((contentType === 'notes' || contentType === 'content') && generatedContent.value) {
          try {
            const examName = exams.value.find(e => e.id === saveTarget.examId)?.name || 'Exam'
            const subjectName = saveSubjects.value.find(s => s.id === saveTarget.subjectId)?.name || 'Subject'
            const topicName = saveTopics.value.find(t => t.id === saveTarget.topicId)?.name || 'Topic'
            const lessonName = saveLessons.value.find(l => l.id === saveTarget.lessonId)?.title || ''
            
            // If lesson is selected, save as lesson notes using the dedicated endpoint
            if (saveTarget.lessonId) {
              // Use the lesson notes endpoint
              const lessonNotesPayload = {
                notes: generatedContent.value
              }
              
              try {
                await api.post(`/v1/admin/lessons/${saveTarget.lessonId}/notes`, lessonNotesPayload)
                toast.success(`Notes added to lesson "${lessonName}"`)
                successCount = 1
              } catch (lessonErr) {
                // If lesson notes endpoint fails, fall back to creating a note
                console.log('Lesson notes endpoint failed, creating note instead:', lessonErr)
                const notePayload = {
                  title: `${lessonName} - AI Generated Content`,
                  content: generatedContent.value,
                  topic_id: saveTarget.topicId,
                  lesson_id: saveTarget.lessonId,
                  status: 'draft',
                  note_type: 'lesson_content'
                }
                await api.post('/v1/admin/notes', notePayload)
                successCount = 1
              }
            } else {
              // No lesson selected - save as topic-level notes
              let noteTitle = ''
              let noteType = 'study_notes'
              
              if (contentType === 'content') {
                noteTitle = `${topicName} - AI Generated Content`
                noteType = 'topic_content'
              } else {
                noteTitle = `${topicName} - AI Generated Notes`
                noteType = 'study_notes'
              }
              
              const notePayload = {
                title: noteTitle,
                content: generatedContent.value,
                topic_id: saveTarget.topicId,
                lesson_id: null,
                status: 'draft',
                note_type: noteType
              }
              
              await api.post('/v1/admin/notes', notePayload)
              successCount = 1
            }
          } catch (e) {
            console.error('Failed to save notes/content:', e)
            errorCount = 1
          }
        } else {
          // For flashcard deck type: create deck first, then batch add cards
          if (contentType === 'flashcards' && generatedTestMeta.value?.deck_title) {
            try {
              const deckPayload = {
                title: generatedTestMeta.value.deck_title,
                description: generatedTestMeta.value.deck_description || '',
                exam_id: saveTarget.examId,
                subject_id: saveTarget.subjectId || undefined,
                topic_id: saveTarget.topicId || undefined,
                lesson_id: saveTarget.lessonId || undefined
              }
              
              const deckRes = await api.post('/v1/admin/flashcard-decks', deckPayload)
              const deckId = deckRes.data?.data?.id || deckRes.data?.id
              
              if (deckId) {
                // Batch add cards to the deck
                const cardsPayload = {
                  cards: itemsToSave.map(item => ({
                    front: item.front,
                    back: item.back,
                    hint: item.hint || undefined,
                    difficulty: item.difficulty || 'medium',
                    tags: item.tags || []
                  }))
                }
                
                await api.post(`/v1/admin/flashcard-decks/${deckId}/cards/batch`, cardsPayload)
                successCount = itemsToSave.length
                toast.success(`Created deck "${deckPayload.title}" with ${successCount} cards!`)
              }
            } catch (e) {
              console.error('Failed to create flashcard deck:', e)
              errorCount = itemsToSave.length
            }
          } else {
          // Save individual items
          for (const item of itemsToSave) {
            try {
              let endpoint = ''
              let payload = { ...item }
              
              switch (contentType) {
                case 'questions':
                  endpoint = '/v1/admin/questions'
                  payload.test_id = testId || null
                  payload.topic_id = saveTarget.topicId
                  // Add lesson_id if a lesson is selected
                  if (saveTarget.lessonId) {
                    payload.lesson_id = saveTarget.lessonId
                  }
                  // question_type must be one of: mcq, numerical, match, subjective
                  // Determine based on whether question has options
                  const validQuestionTypes = ['mcq', 'numerical', 'match', 'subjective']
                  if (payload.question_type && validQuestionTypes.includes(payload.question_type)) {
                    // Keep existing valid question_type
                  } else if (payload.options && Object.keys(payload.options).length >= 2) {
                    payload.question_type = 'mcq'
                  } else {
                    payload.question_type = 'mcq' // Default to mcq
                  }
                  // API expects 'content' not 'question_text'
                  if (payload.question_text && !payload.content) {
                    payload.content = payload.question_text
                    delete payload.question_text
                  }
                  payload.marks = payload.marks || 1
                  payload.status = payload.status || 'draft'
                  // Only stringify options for MCQ type
                  if (payload.question_type === 'mcq' && payload.options && typeof payload.options === 'object') {
                    payload.options = JSON.stringify(payload.options)
                  } else if (payload.question_type !== 'mcq') {
                    delete payload.options // Remove options for non-MCQ types
                  }
                  break
                  
                case 'subjects':
                  endpoint = '/v1/admin/subjects'
                  payload.exam_id = saveTarget.examId
                  payload.is_active = true
                  break
                  
                case 'topics':
                  endpoint = '/v1/admin/topics'
                  payload.subject_id = saveTarget.subjectId
                  payload.status = payload.status || 'draft'
                  break
                  
                case 'lessons':
                  endpoint = '/v1/admin/lessons'
                  payload.topic_id = saveTarget.topicId
                  payload.status = payload.status || 'draft'
                  // Map duration_minutes to duration if needed
                  if (payload.duration_minutes && !payload.duration) {
                    payload.duration = payload.duration_minutes
                  }
                  break
                  
                case 'flashcards':
                  // Check if we have deck metadata (from flashcard_deck generation)
                  if (generatedTestMeta.value?.deck_title && !saveTarget.testId) {
                    // This is handled by the batch save below
                    continue
                  }
                  endpoint = '/v1/admin/flashcards'
                  payload.topic_id = saveTarget.topicId
                  if (saveTarget.lessonId) payload.lesson_id = saveTarget.lessonId
                  payload.status = payload.status || 'draft'
                  break
                  
                case 'notes':
                  endpoint = '/v1/admin/notes'
                  payload.topic_id = saveTarget.topicId
                  if (saveTarget.lessonId) payload.lesson_id = saveTarget.lessonId
                  payload.status = payload.status || 'draft'
                  break
              }
              
              if (endpoint) {
                await api.post(endpoint, payload)
                successCount++
              }
            } catch (e) {
              console.error('Failed to save item:', e)
              errorCount++
            }
          }
          } // close flashcard deck else
        }
        
        // Show toast notifications
        if (successCount > 0 && errorCount === 0) {
          toast.success(`Successfully saved ${successCount} ${contentType}!`)
        } else if (successCount > 0 && errorCount > 0) {
          toast.warning(`Saved ${successCount} ${contentType}, but ${errorCount} failed`)
        } else {
          toast.error(`Failed to save ${contentType}. Please try again.`)
        }
        
        closeSaveModal()
        
      } catch (e) {
        console.error('Save error:', e)
        toast.error(`Error saving content: ${e.response?.data?.message || e.message}`)
      } finally {
        savingContent.value = false
      }
    }

    // ==================== CREATE MODE FUNCTIONS ====================
    const openCreateModal = async (card) => {
      activeCreateCard.value = card
      Object.keys(createForm).forEach(k => delete createForm[k])
      filteredSubjects.value = []
      filteredTopics.value = []
      filteredLessons.value = []
      
      // Load required data for selects
      if (card.fields.some(f => f.options === 'exams')) await loadExams()
      if (card.fields.some(f => f.options === 'subjects')) await loadAllSubjects()
      if (card.fields.some(f => f.options === 'topics')) await loadAllTopics()
      if (card.fields.some(f => f.options === 'lessons')) await loadAllLessons()
      if (card.fields.some(f => f.options === 'tests')) await loadAllTests()
      if (card.fields.some(f => f.options === 'flashcardDecks')) await loadAllFlashcardDecks()
      
      showCreateModal.value = true
    }

    const closeCreateModal = () => {
      showCreateModal.value = false
      activeCreateCard.value = null
      Object.keys(createForm).forEach(k => delete createForm[k])
    }

    const handleSelectChange = async (field) => {
      if (field.triggers === 'subjects' && createForm.exam_id) {
        await loadSubjectsForExam(createForm.exam_id)
        createForm.subject_id = ''
        createForm.topic_id = ''
        createForm.lesson_id = ''
        filteredTopics.value = []
        filteredLessons.value = []
      }
      if (field.triggers === 'topics' && createForm.subject_id) {
        await loadTopicsForSubject(createForm.subject_id)
        createForm.topic_id = ''
        createForm.lesson_id = ''
        filteredLessons.value = []
      }
      if (field.triggers === 'lessons' && createForm.topic_id) {
        await loadLessonsForTopic(createForm.topic_id)
        createForm.lesson_id = ''
      }
    }

    const getFieldOptions = (field) => {
      const staticOptions = {
        answers: [
          { value: 'a', label: 'Option A' },
          { value: 'b', label: 'Option B' },
          { value: 'c', label: 'Option C' },
          { value: 'd', label: 'Option D' }
        ],
        difficulty: [
          { value: 'easy', label: 'Easy' },
          { value: 'medium', label: 'Medium' },
          { value: 'hard', label: 'Hard' }
        ],
        questionTypes: [
          { value: 'mcq', label: 'Multiple Choice (MCQ)' },
          { value: 'numerical', label: 'Numerical' },
          { value: 'match', label: 'Match' },
          { value: 'subjective', label: 'Subjective' }
        ],
        languages: LANGUAGE_OPTIONS
      }
      
      if (staticOptions[field.options]) return staticOptions[field.options]
      
      switch (field.options) {
        case 'exams': return exams.value.map(e => ({ value: e.id, label: e.name }))
        case 'subjects': return allSubjects.value.map(s => ({ value: s.id, label: s.name }))
        case 'topics': return allTopics.value.map(t => ({ value: t.id, label: t.name }))
        case 'lessons': return allLessons.value.map(l => ({ value: l.id, label: l.title }))
        case 'tests': return allTests.value.map(t => ({ value: t.id, label: t.title }))
        case 'flashcardDecks': return allFlashcardDecks.value.map(d => ({ value: d.id, label: d.title }))
        case 'filteredSubjects': return filteredSubjects.value.map(s => ({ value: s.id, label: s.name }))
        case 'filteredTopics': return filteredTopics.value.map(t => ({ value: t.id, label: t.name }))
        case 'filteredLessons': return filteredLessons.value.map(l => ({ value: l.id, label: l.title }))
        default: return []
      }
    }

    const canSubmitCreate = computed(() => {
      if (!activeCreateCard.value) return false
      return activeCreateCard.value.fields
        .filter(f => f.required)
        .every(f => createForm[f.name])
    })

    const aiAssistCreate = async () => {
      if (!activeCreateCard.value) return
      creatingWithAI.value = true
      
      try {
        const card = activeCreateCard.value
        let prompt = `Generate content for creating a ${card.title}. `
        
        // Add context from filled fields
        card.fields.forEach(f => {
          if (createForm[f.name]) {
            const displayValue = getDisplayValue(f, createForm[f.name])
            prompt += `${f.label}: ${displayValue}. `
          }
        })
        
        prompt += `Please provide suggestions for the empty fields in a structured format.`
        
        const response = await api.post('/v1/admin/admin-chat', { 
          message: prompt,
          session_id: sessionId.value 
        }, { timeout: 120000 })
        
        if (response.data.session_id) sessionId.value = response.data.session_id
        
        const aiContent = response.data.response
        
        // Try to extract and fill form fields from AI response
        fillFormFromAI(aiContent, card)
        
        addSystemMessage(`AI Suggestion: ${aiContent}`)
        
      } catch (e) {
        console.error('AI assist failed:', e)
        addSystemMessage(`❌ AI assist failed: ${e.message}`)
      } finally {
        creatingWithAI.value = false
      }
    }

    const getDisplayValue = (field, value) => {
      if (field.type === 'select') {
        const options = getFieldOptions(field)
        const opt = options.find(o => o.value === value)
        return opt?.label || value
      }
      return value
    }

    const fillFormFromAI = (content, card) => {
      // Try to extract values from AI response
      card.fields.forEach(f => {
        if (createForm[f.name]) return // Skip already filled
        
        // Try different patterns to extract values
        const patterns = [
          new RegExp(`${f.label}[:\\s]+([^\\n]+)`, 'i'),
          new RegExp(`${f.name}[:\\s]+([^\\n]+)`, 'i'),
          new RegExp(`"${f.name}"[:\\s]+"([^"]+)"`, 'i')
        ]
        
        for (const pattern of patterns) {
          const match = content.match(pattern)
          if (match && match[1]) {
            const value = match[1].trim().replace(/^["']|["']$/g, '')
            if (value && f.type !== 'select') {
              createForm[f.name] = value
              break
            }
          }
        }
      })
    }

    const submitCreate = async () => {
      if (!canSubmitCreate.value || !activeCreateCard.value) return
      
      loading.value = true
      loadingText.value = 'Creating'
      
      try {
        const card = activeCreateCard.value
        const payload = { ...createForm }
        
        // Clean up internal fields
        delete payload._pendingQuestions
        
        // Handle question options specially
        if (card.type === 'question') {
          // question_type must be one of: mcq, numerical, match, subjective
          const validQuestionTypes = ['mcq', 'numerical', 'match', 'subjective']
          const questionType = validQuestionTypes.includes(payload.question_type) ? payload.question_type : 'mcq'
          // Only add options for MCQ type
          if (questionType === 'mcq') {
            payload.options = JSON.stringify({
              a: createForm.option_a || '',
              b: createForm.option_b || '',
              c: createForm.option_c || '',
              d: createForm.option_d || ''
            })
          }
          delete payload.option_a
          delete payload.option_b
          delete payload.option_c
          delete payload.option_d
          delete payload.type // Remove type field, only use question_type
          payload.question_type = questionType
          payload.marks = payload.marks || 1
        }
        
        // Handle test creation - ensure duration_minutes is set
        if (card.type === 'test') {
          // duration_minutes is now the form field name, ensure it has a default
          payload.duration_minutes = payload.duration_minutes || 30
        }
        
        // Handle nested endpoints
        let endpoint = card.endpoint
        if (card.type === 'subject' && createForm.exam_id) {
          endpoint = '/v1/admin/subjects'
          // Keep exam_id in payload — the API expects it in the body
        }
        if (card.type === 'topic' && createForm.subject_id) {
          endpoint = `/v1/admin/subjects/${createForm.subject_id}/topics`
          delete payload.subject_id
          delete payload.exam_id
        }
        if (card.type === 'lesson' && createForm.topic_id) {
          endpoint = `/v1/admin/topics/${createForm.topic_id}/lessons`
          delete payload.topic_id
          delete payload.subject_id
          delete payload.exam_id
        }
        if (card.type === 'flashcard_card' && createForm.deck_id) {
          endpoint = `/v1/admin/flashcard-decks/${createForm.deck_id}/cards`
          delete payload.deck_id
        }
        
        const response = await api.post(endpoint, payload)
        const createdItem = response.data
        
        closeCreateModal()
        addSystemMessage(`✅ ${card.title} "${payload.name || payload.title}" created successfully!`)
        
        // Handle pending questions for test creation
        if (card.type === 'test' && createForm._pendingQuestions?.length) {
          const testId = createdItem.id || createdItem.test?.id
          if (testId) {
            addSystemMessage(`Adding ${createForm._pendingQuestions.length} questions to the test...`)
            let added = 0
            const validQuestionTypes = ['mcq', 'numerical', 'match', 'subjective']
            for (const q of createForm._pendingQuestions) {
              try {
                // question_type must be one of: mcq, numerical, match, subjective
                let questionType = 'mcq' // Default
                if (q.question_type && validQuestionTypes.includes(q.question_type)) {
                  questionType = q.question_type
                } else if (q.options && Object.keys(q.options).length >= 2) {
                  questionType = 'mcq'
                }
                const questionPayload = {
                  test_id: testId,
                  content: q.question_text || q.content,
                  question_type: questionType,
                  correct_answer: q.correct_answer,
                  explanation: q.explanation,
                  difficulty: q.difficulty || 'medium',
                  marks: q.marks || 1
                }
                // Add lesson_id if available from the test creation form
                if (createForm.lesson_id) {
                  questionPayload.lesson_id = createForm.lesson_id
                }
                // Only add options for MCQ type
                if (questionType === 'mcq' && q.options) {
                  questionPayload.options = JSON.stringify(q.options)
                }
                await api.post('/v1/admin/questions', questionPayload)
                added++
              } catch (e) { /* continue */ }
            }
            addSystemMessage(`✅ Added ${added} questions to the test!`)
          }
        }
        
        // Refresh relevant data
        if (card.type === 'exam') await loadExams()
        if (card.type === 'subject') await loadAllSubjects()
        if (card.type === 'topic') await loadAllTopics()
        if (card.type === 'lesson') await loadAllLessons()
        if (card.type === 'test') await loadAllTests()
        if (card.type === 'flashcard_deck') await loadAllFlashcardDecks()
        if (card.type === 'flashcard_card') await loadAllFlashcardDecks()
        
      } catch (e) {
        const errorMsg = e.response?.data?.error?.message || e.message
        addSystemMessage(`❌ Failed to create: ${errorMsg}`)
      } finally {
        loading.value = false
      }
    }

    // ==================== GENERATE MODE FUNCTIONS ====================
    const currentContextPath = computed(() => {
      const parts = []
      if (selectedExam.value) {
        const exam = exams.value.find(e => e.id === selectedExam.value)
        if (exam) parts.push(exam.name)
      }
      if (selectedSubject.value) {
        const subject = subjects.value.find(s => s.id === selectedSubject.value)
        if (subject) parts.push(subject.name)
      }
      if (selectedTopic.value) {
        const topic = topics.value.find(t => t.id === selectedTopic.value)
        if (topic) parts.push(topic.name)
      }
      if (selectedLesson.value) {
        const lesson = lessons.value.find(l => l.id === selectedLesson.value)
        if (lesson) parts.push(lesson.title)
      }
      return parts.join(' → ')
    })

    const generateContextHint = computed(() => {
      if (!selectedExam.value) return 'Select an exam to generate content'
      if (!selectedSubject.value) return 'Generate subjects & syllabus for this exam'
      if (!selectedTopic.value) return 'Generate topics & content for this subject'
      if (!selectedLesson.value) return 'Generate lessons & materials for this topic'
      return 'Generate content for this lesson'
    })

    const availableGenerateActions = computed(() => {
      if (selectedExam.value && !selectedSubject.value) {
        return [
          { type: 'subjects', icon: 'book', label: 'Generate Subjects', primary: true },
          { type: 'full_syllabus', icon: 'list', label: 'Generate Full Syllabus', primary: true },
          { type: 'pyq_exam', icon: 'archive', label: 'Previous Year Questions (25 Years)' },
          { type: 'full_mock_test', icon: 'award', label: 'Generate Full Mock Test' },
          { type: 'test_series', icon: 'layers', label: 'Generate Test Series' },
          { type: 'exam_overview', icon: 'file-text', label: 'Generate Exam Overview' }
        ]
      }
      
      if (selectedSubject.value && !selectedTopic.value) {
        return [
          { type: 'topics', icon: 'hash', label: 'Generate Topics', primary: true },
          { type: 'syllabus', icon: 'list', label: 'Generate Syllabus', primary: true },
          { type: 'pyq_subject', icon: 'archive', label: 'Previous Year Questions' },
          { type: 'subject_notes', icon: 'edit-3', label: 'Generate Subject Notes' },
          { type: 'subject_questions', icon: 'help-circle', label: 'Generate Questions' }
        ]
      }
      
      if (selectedTopic.value && !selectedLesson.value) {
        return [
          { type: 'lessons', icon: 'file-text', label: 'Generate Lessons', primary: true },
          { type: 'notes', icon: 'edit-3', label: 'Generate Notes', primary: true },
          { type: 'pyq_topic', icon: 'archive', label: 'Previous Year Questions' },
          { type: 'questions', icon: 'help-circle', label: 'Generate MCQs' },
          { type: 'flashcards', icon: 'layers', label: 'Generate Flashcards' },
          { type: 'flashcard_deck', icon: 'layers', label: 'Create Flashcard Deck' },
          { type: 'test', icon: 'clipboard', label: 'Generate Test' }
        ]
      }
      
      if (selectedLesson.value) {
        return [
          { type: 'lesson_content', icon: 'file-text', label: 'Generate Content', primary: true },
          { type: 'lesson_notes', icon: 'edit-3', label: 'Generate Notes' },
          { type: 'lesson_questions', icon: 'help-circle', label: 'Generate MCQs' },
          { type: 'lesson_flashcards', icon: 'layers', label: 'Generate Flashcards' },
          { type: 'lesson_flashcard_deck', icon: 'layers', label: 'Create Flashcard Deck' },
          { type: 'lesson_test', icon: 'clipboard', label: 'Generate Quiz' }
        ]
      }
      
      return []
    })

    const generateContent = async (action) => {
      loading.value = true
      loadingText.value = 'Generating'
      
      try {
        const examName = exams.value.find(e => e.id === selectedExam.value)?.name || ''
        const subjectName = subjects.value.find(s => s.id === selectedSubject.value)?.name || ''
        const topicName = topics.value.find(t => t.id === selectedTopic.value)?.name || ''
        const lessonName = lessons.value.find(l => l.id === selectedLesson.value)?.title || ''

        const prompts = {
          // Exam level
          subjects: `Generate a comprehensive list of subjects for the "${examName}" examination based on the LATEST OFFICIAL SYLLABUS (2024-2025). For each subject provide name, description, importance level based on exam weightage, and estimated study hours. Return as a structured list.`,
          full_syllabus: `Generate the COMPLETE syllabus structure for "${examName}" using the LATEST OFFICIAL SYLLABUS (2024-2025). Include ALL subjects, topics, subtopics, learning objectives, and estimated study hours. Organize in logical learning order.`,
          test_series: `Design a complete test series plan for "${examName}" preparation with sectional tests, full-length mock tests, topic-wise tests, revision tests, suggested timeline, and performance tracking suggestions.`,
          exam_overview: `Write a comprehensive overview of "${examName}" examination including exam pattern, marking scheme, subject-wise weightage, preparation strategy, tips for success, and common mistakes to avoid.`,
          
          // Subject level
          topics: `Generate ALL topics for "${subjectName}" in "${examName}" based on the LATEST OFFICIAL SYLLABUS (2024-2025). For each topic provide name, detailed description (50-100 words), importance level, key concepts, and estimated study hours. Organize in logical learning order.`,
          syllabus: `Generate the COMPLETE syllabus for "${subjectName}" in "${examName}" using the LATEST OFFICIAL SYLLABUS (2024-2025). Include ALL topics, subtopics with learning objectives, estimated study hours, and importance levels.`,
          subject_notes: `Create CONCISE but COMPREHENSIVE revision notes for "${subjectName}" covering: Key Concepts at a Glance, Formula Sheet (use LaTeX $formula$), Important Values & Constants, Quick Facts, Memory Aids, Previous Year Insights, and Last-Minute Revision Checklist.`,
          subject_questions: `Generate 20 HIGH-QUALITY MCQ questions for "${subjectName}" in "${examName}". Each question MUST have 4 options (a,b,c,d) with lowercase keys, correct_answer, explanation (100-200 words), shortcut_method, trap_analysis, memory_hack, and difficulty level. Mix: Easy 25%, Medium 50%, Hard 25%.`,
          
          // Topic level
          lessons: `Create detailed lesson plans for "${topicName}" under "${subjectName}". Each lesson should include title, learning objectives, content outline, duration (30-60 mins), and key takeaways. Organize in logical learning sequence.`,
          notes: `Create CONCISE but COMPREHENSIVE revision notes for "${topicName}" with: Key Concepts at a Glance, Formula Sheet (use LaTeX $formula$), Important Values & Constants, Quick Facts, Memory Aids with mnemonics, Previous Year Insights, and Last-Minute Revision Checklist.`,
          questions: `Generate 15 HIGH-QUALITY MCQ questions for "${topicName}". Each question MUST have 4 options (a,b,c,d) with lowercase keys, correct_answer, explanation (100-200 words), shortcut_method (50-100 words), trap_analysis (50-100 words), memory_hack (30-50 words), difficulty, and marks: 1. Mix: Easy 30%, Medium 50%, Hard 20%.`,
          flashcards: `Generate 20 HIGH-QUALITY flashcards for "${topicName}". Each card MUST have: front (max 200 chars), back (max 500 chars, use LaTeX $formula$ for math), memory_hack (MANDATORY, unique mnemonic), hint, difficulty, and tags. Mix: Easy 30%, Medium 50%, Hard 20%.`,
          test: `Create a complete test for "${topicName}" with 20 questions, 30 minutes duration. Each question MUST have 4 options (a,b,c,d), correct_answer, explanation, shortcut_method, trap_analysis, memory_hack, difficulty, and marks. Mix: Easy 30%, Medium 50%, Hard 20%.`,
          
          // Lesson level
          lesson_content: `Create a COMPREHENSIVE, exam-focused lesson for "${lessonName}". Include all 8 sections: Introduction, Core Concepts, Important Formulas & Theorems (use LaTeX $formula$), Solved Examples (3-5), Memory Techniques, Common Mistakes & Misconceptions, Exam Tips, and Quick Revision Points.`,
          lesson_notes: `Create CONCISE revision notes for "${lessonName}" with: Key Concepts at a Glance, Formula Sheet (use LaTeX $formula$), Quick Facts, Memory Aids with mnemonics, and Last-Minute Revision Checklist.`,
          lesson_questions: `Generate 10 HIGH-QUALITY MCQ questions for "${lessonName}". Each question MUST have 4 options (a,b,c,d) with lowercase keys, correct_answer, explanation (100-200 words), shortcut_method, trap_analysis, memory_hack, difficulty, and marks: 1.`,
          lesson_flashcards: `Generate 15 HIGH-QUALITY flashcards for "${lessonName}". Each card MUST have: front (max 200 chars), back (max 500 chars), memory_hack (MANDATORY), hint, difficulty, and tags.`,
          lesson_test: `Create a focused quiz for "${lessonName}" with 10 questions, 15 minutes duration. Each question MUST have 4 options (a,b,c,d), correct_answer, explanation, shortcut_method, trap_analysis, memory_hack, difficulty, and marks: 1.`
        }

        const prompt = prompts[action.type] || `Generate ${action.type} content for the selected context.`
        
        activeMode.value = 'chat'
        inputMessage.value = prompt
        await sendMessage()
        
      } catch (e) {
        console.error('Generate failed:', e)
        addSystemMessage(`❌ Generation failed: ${e.message}`)
      } finally {
        loading.value = false
      }
    }

    // ==================== INLINE GENERATE FUNCTIONS ====================
    const generateContentInline = async (action) => {
      generating.value = true
      generatingType.value = action.type
      generatingText.value = `Generating ${action.label.replace('Generate ', '')}...`
      lastGenerateAction.value = action
      flippedCards.value = []
      
      // Clear previous content
      generatedContent.value = ''
      generatedItems.value = []
      generatedTestMeta.value = null
      
      try {
        // Get full context names
        const examName = exams.value.find(e => e.id === selectedExam.value)?.name || ''
        const examDesc = exams.value.find(e => e.id === selectedExam.value)?.description || ''
        const subjectName = subjects.value.find(s => s.id === selectedSubject.value)?.name || ''
        const subjectDesc = subjects.value.find(s => s.id === selectedSubject.value)?.description || ''
        const topicName = topics.value.find(t => t.id === selectedTopic.value)?.name || ''
        const topicDesc = topics.value.find(t => t.id === selectedTopic.value)?.description || ''
        const lessonName = lessons.value.find(l => l.id === selectedLesson.value)?.title || ''
        const lessonDesc = lessons.value.find(l => l.id === selectedLesson.value)?.description || ''

        // Build context string based on what's selected
        const buildContext = () => {
          let ctx = []
          if (examName) ctx.push(`Exam: "${examName}"${examDesc ? ` (${examDesc})` : ''}`)
          if (subjectName) ctx.push(`Subject: "${subjectName}"${subjectDesc ? ` - ${subjectDesc}` : ''}`)
          if (topicName) ctx.push(`Topic: "${topicName}"${topicDesc ? ` - ${topicDesc}` : ''}`)
          if (lessonName) ctx.push(`Lesson: "${lessonName}"${lessonDesc ? ` - ${lessonDesc}` : ''}`)
          return ctx.length > 0 ? `\n\nContext:\n${ctx.join('\n')}` : ''
        }
        
        const context = buildContext()
        const contextBrief = [examName, subjectName, topicName, lessonName].filter(Boolean).join(' > ')

        // Language instruction
        const langCode = selectedLanguage.value || 'en'
        const langLabel = LANGUAGE_OPTIONS.find(l => l.value === langCode)?.label || 'English'
        const langInstruction = langCode !== 'en' 
          ? `\n\nIMPORTANT: Generate ALL content in ${langLabel} language. The questions, options, explanations, titles, descriptions, and all text content MUST be written in ${langLabel}. Only JSON keys should remain in English.`
          : ''

        // Build enhanced prompts with full context
        const prompts = {
          subjects: `Generate a comprehensive list of subjects for the "${examName}" examination based on the LATEST OFFICIAL SYLLABUS (2024-2025).
${context}

CRITICAL REQUIREMENTS:
- Each subject should be relevant to the official exam syllabus
- Include a mix of core and optional subjects if applicable
- Provide accurate estimated study hours
- Mark importance based on exam weightage

Return ONLY a valid JSON array (no markdown, no explanation):
[{"name": "Subject Name", "description": "Comprehensive description of what this subject covers", "importance": "high", "estimated_hours": 50, "key_areas": ["key area 1", "key area 2", "key area 3"]}]`,
          
          topics: `Generate comprehensive topics for the subject "${subjectName}" based on the LATEST OFFICIAL SYLLABUS (2024-2025).
${context}

CRITICAL REQUIREMENTS:
- Topics should cover the complete syllabus for this subject
- Arrange in logical learning sequence (foundational concepts first)
- Include both theoretical and practical aspects
- Estimate hours based on topic complexity

Return ONLY a valid JSON array (no markdown, no explanation):
[{"name": "Topic Name", "description": "Detailed description of topic coverage", "importance": "high", "estimated_hours": 10, "key_concepts": ["concept1", "concept2", "concept3"]}]`,
          
          lessons: `Create detailed lesson plans for the topic "${topicName}".
${context}

CRITICAL REQUIREMENTS:
- Each lesson should be self-contained but build on previous lessons
- Include clear learning objectives
- Provide realistic duration estimates
- Focus on practical understanding
- Organize in logical learning sequence

Return ONLY a valid JSON array (no markdown, no explanation):
[{"title": "Lesson Title", "description": "What students will learn in this lesson", "duration_minutes": 45, "learning_objectives": ["objective 1", "objective 2"], "key_takeaways": ["takeaway 1", "takeaway 2"]}]`,
          
          questions: `Generate 15 HIGH-QUALITY MCQ questions about "${topicName}".
${context}

CRITICAL REQUIREMENTS:
1. Questions MUST be exam-pattern and difficulty appropriate
2. Mix of difficulty levels:
   - Easy (30%): Direct application of concepts
   - Medium (50%): Multi-step problems, concept combinations
   - Hard (20%): Tricky problems, multi-concept integration
3. Each question MUST have exactly 4 options (a, b, c, d) using lowercase keys
4. Only ONE correct answer per question
5. Plausible distractors based on common student mistakes
6. Difficulty level: mixed
7. Question type: mcq
8. For numerical questions: use LaTeX for formulas ($formula$), include units

For EACH question provide ALL of these fields:
- question_text: Complete question with all necessary data
- options: {"a": "...", "b": "...", "c": "...", "d": "..."}
- correct_answer: Single lowercase letter (a/b/c/d)
- explanation: Detailed step-by-step solution (100-200 words)
- shortcut_method: Quick solving technique if applicable (50-100 words)
- trap_analysis: Why students might choose each wrong option — per-option breakdown (50-100 words)
- memory_hack: Mnemonic or tip to remember the concept (30-50 words)
- difficulty: "easy", "medium", or "hard"
- marks: 1

Return ONLY valid JSON array (no markdown, no explanation):
[{"question_text": "Complete question text with all data", "options": {"a": "Option A", "b": "Option B", "c": "Option C", "d": "Option D"}, "correct_answer": "a", "explanation": "Detailed step-by-step solution", "shortcut_method": "Quick solving technique", "trap_analysis": "Option B: [specific mistake]. Option C: [specific mistake]. Option D: [specific mistake].", "memory_hack": "Memory tip for this concept", "difficulty": "medium", "marks": 1}]

IMPORTANT: Generate exactly 15 questions covering different aspects.
ALL fields (explanation, shortcut_method, trap_analysis, memory_hack) are MANDATORY.`,
          
          subject_questions: `Generate 20 HIGH-QUALITY MCQ questions covering "${subjectName}".
${context}

CRITICAL REQUIREMENTS:
1. Questions should span across all major topics of the subject
2. Mix of difficulty levels:
   - Easy (25%): Direct application of concepts
   - Medium (50%): Multi-step problems, concept combinations
   - Hard (25%): Tricky problems, multi-concept integration
3. Each question MUST have exactly 4 options (a, b, c, d) using lowercase keys
4. Only ONE correct answer per question
5. Plausible distractors based on common student mistakes
6. For numerical questions: use LaTeX for formulas ($formula$), include units

For EACH question provide ALL of these fields:
- question_text: Complete question with all necessary data
- options: {"a": "...", "b": "...", "c": "...", "d": "..."}
- correct_answer: Single lowercase letter (a/b/c/d)
- explanation: Detailed step-by-step solution (100-200 words)
- shortcut_method: Quick solving technique if applicable (50-100 words)
- trap_analysis: Why students might choose each wrong option — per-option breakdown (50-100 words)
- memory_hack: Mnemonic or tip to remember the concept (30-50 words)
- difficulty: "easy", "medium", or "hard"
- marks: 1

Return ONLY valid JSON array (no markdown, no explanation):
[{"question_text": "Question text?", "options": {"a": "Option A", "b": "Option B", "c": "Option C", "d": "Option D"}, "correct_answer": "a", "explanation": "Detailed step-by-step solution", "shortcut_method": "Quick solving technique", "trap_analysis": "Per-option breakdown of wrong answer traps", "memory_hack": "Memory tip for this concept", "difficulty": "medium", "marks": 1}]

IMPORTANT: Generate exactly 20 questions. ALL fields (explanation, shortcut_method, trap_analysis, memory_hack) are MANDATORY.`,
          
          flashcards: `Generate 20 HIGH-QUALITY flashcards for: ${topicName}
${context}

CRITICAL REQUIREMENTS:
1. Each flashcard should test ONE specific concept, formula, definition, or fact
2. Front side should be a clear, specific question or prompt (max 200 chars)
3. Back side should be a concise but complete answer (max 500 chars, use LaTeX $formula$ for math)
4. Mix of difficulty levels: easy (30%), medium (50%), hard (20%)

Types of flashcards to include:
- Definition cards: "What is X?" / "X is..."
- Formula cards: "Formula for X?" / "$formula$ where..."
- Concept cards: "Explain X" / "Concise explanation"
- Comparison cards: "Difference between X and Y?" / "Key differences..."
- Application cards: "When to use X?" / "Use X when..."
- Fact cards: "Value of X?" / "X = value"

For EACH flashcard provide ALL of these fields:
- front: Clear question or prompt (max 200 chars)
- back: Concise but complete answer (max 500 chars, use LaTeX $formula$ for math)
- memory_hack: A UNIQUE mnemonic, visual trick, rhyme, analogy, or pattern to remember this card's answer. Must be SPECIFIC to this card — never generic.
- hint: Optional memory aid or partial clue (max 100 chars, null if not needed)
- difficulty: "easy", "medium", or "hard"
- tags: Array of 1-3 relevant tags (e.g., ["formula", "definition", "important"])

Return ONLY valid JSON array (no markdown, no explanation):
[{"front": "What is the formula for kinetic energy?", "back": "KE = $\\\\frac{1}{2}mv^2$ where m = mass, v = velocity", "memory_hack": "Half of mass times velocity squared — think 'half MV squared'", "hint": "Think: half of mass times velocity squared", "difficulty": "easy", "tags": ["formula", "energy"]}]

IMPORTANT: Generate exactly 20 flashcards covering different aspects. memory_hack is MANDATORY for every card.`,
          
          lesson_flashcards: `Generate 15 HIGH-QUALITY flashcards for the lesson "${lessonName}".

Lesson Title: ${lessonName}
Lesson Description: ${lessonDesc}
${context}

Generate flashcards specifically aligned with this lesson's content and learning objectives.

CRITICAL REQUIREMENTS:
1. Each flashcard should test ONE specific concept, formula, definition, or fact
2. Front side should be a clear, specific question or prompt (max 200 chars)
3. Back side should be a concise but complete answer (max 500 chars, use LaTeX $formula$ for math)
4. Mix of difficulty levels: easy (30%), medium (50%), hard (20%)

For EACH flashcard provide ALL of these fields:
- front: Clear question or prompt (max 200 chars)
- back: Concise but complete answer (max 500 chars, use LaTeX $formula$ for math)
- memory_hack: A UNIQUE mnemonic, visual trick, rhyme, analogy, or pattern to remember this card's answer.
- hint: Optional memory aid or partial clue (max 100 chars, null if not needed)
- difficulty: "easy", "medium", or "hard"
- tags: Array of 1-3 relevant tags

Return ONLY valid JSON array (no markdown, no explanation):
[{"front": "Key question or term from the lesson", "back": "Answer or explanation", "memory_hack": "Specific mnemonic for this card", "hint": "Optional hint", "difficulty": "easy", "tags": ["definition"]}]

IMPORTANT: Generate exactly 15 flashcards. memory_hack is MANDATORY for every card.`,
          
          flashcard_deck: `Generate a flashcard deck with 20 cards for studying "${topicName}".
${context}

CRITICAL REQUIREMENTS:
1. Each flashcard should test ONE specific concept, formula, definition, or fact
2. Front side should be a clear, specific question or prompt (max 200 chars)
3. Back side should be a concise but complete answer (max 500 chars, use LaTeX $formula$ for math)
4. Mix of difficulty levels: easy (30%), medium (50%), hard (20%)

Types of flashcards to include:
- Definition cards, Formula cards, Concept cards, Comparison cards, Application cards, Fact cards

For EACH flashcard provide ALL of these fields:
- front: Clear question or prompt (max 200 chars)
- back: Concise but complete answer (max 500 chars, use LaTeX $formula$ for math)
- memory_hack: A UNIQUE mnemonic, visual trick, rhyme, analogy, or pattern to remember this card's answer.
- hint: Optional memory aid or partial clue (max 100 chars, null if not needed)
- difficulty: "easy", "medium", or "hard"
- tags: Array of 1-3 relevant tags

Return ONLY valid JSON with this exact format:
{"deck_title": "${topicName} - Flashcard Deck", "deck_description": "Key concepts and revision cards for ${topicName}", "cards": [{"front": "Question or term", "back": "Answer or definition", "memory_hack": "Specific mnemonic for this card", "hint": "Optional hint", "difficulty": "easy", "tags": ["formula"]}]}

IMPORTANT: Generate exactly 20 flashcards. memory_hack is MANDATORY for every card.`,
          
          lesson_flashcard_deck: `Generate a flashcard deck with 15 cards for the lesson "${lessonName}".

Lesson Title: ${lessonName}
Lesson Description: ${lessonDesc}
${context}

Generate flashcards specifically aligned with this lesson's content and learning objectives.

CRITICAL REQUIREMENTS:
1. Focus specifically on this lesson's key points and takeaways
2. Front side should be a clear, specific question or prompt (max 200 chars)
3. Back side should be a concise but complete answer (max 500 chars, use LaTeX $formula$ for math)
4. Mix of difficulty levels: easy (30%), medium (50%), hard (20%)

For EACH flashcard provide ALL of these fields:
- front: Clear question or prompt (max 200 chars)
- back: Concise but complete answer (max 500 chars, use LaTeX $formula$ for math)
- memory_hack: A UNIQUE mnemonic, visual trick, rhyme, analogy, or pattern to remember this card's answer.
- hint: Optional memory aid or partial clue (max 100 chars, null if not needed)
- difficulty: "easy", "medium", or "hard"
- tags: Array of 1-3 relevant tags

Return ONLY valid JSON with this exact format:
{"deck_title": "${lessonName} - Flashcard Deck", "deck_description": "Revision cards for ${lessonName}", "cards": [{"front": "Question or term", "back": "Answer or definition", "memory_hack": "Specific mnemonic for this card", "hint": "Optional hint", "difficulty": "easy", "tags": ["definition"]}]}

IMPORTANT: Generate exactly 15 flashcards. memory_hack is MANDATORY for every card.`,
          
          test: `Create a complete test titled "${topicName} - Assessment" with 20 questions.
Duration: 30 minutes, Total marks: 20
${context}

CRITICAL REQUIREMENTS:
1. Questions MUST be exam-pattern appropriate
2. Mix of difficulty levels:
   - Easy (30%): Direct application, 6 questions
   - Medium (50%): Multi-step problems, 10 questions
   - Hard (20%): Advanced/tricky, 4 questions
3. Each question MUST have exactly 4 options (a, b, c, d) with lowercase keys
4. Only ONE correct answer per question
5. Include detailed explanations, shortcut methods, trap analysis, and memory hacks

For EACH question provide:
- question_text: Complete question with all necessary data (use $LaTeX$ for math)
- question_type: "mcq"
- options: {"a": "...", "b": "...", "c": "...", "d": "..."}
- correct_answer: Single lowercase letter (a/b/c/d)
- explanation: Detailed step-by-step solution (100-200 words)
- shortcut_method: Quick solving technique (50-100 words)
- trap_analysis: Per-option breakdown of why students choose wrong answers (50-100 words)
- memory_hack: Mnemonic or tip to remember the concept (30-50 words)
- marks: 1
- difficulty: "easy", "medium", or "hard"

Return ONLY valid JSON with this exact format:
{"title": "${topicName} - Assessment", "description": "Test description", "duration": 30, "total_marks": 20, "passing_marks": 8, "questions": [{"question_text": "Question?", "question_type": "mcq", "options": {"a": "A", "b": "B", "c": "C", "d": "D"}, "correct_answer": "a", "explanation": "Detailed step-by-step solution", "shortcut_method": "Quick solving technique", "trap_analysis": "Per-option breakdown", "memory_hack": "Memory tip", "difficulty": "medium", "marks": 1}], "difficulty_breakdown": {"easy": 6, "medium": 10, "hard": 4}}`,
          
          lesson_test: `Create a focused quiz for the lesson "${lessonName}" with 10 questions.
Duration: 15 minutes, Total marks: 10

Lesson Title: ${lessonName}
Lesson Description: ${lessonDesc}
${context}

CRITICAL REQUIREMENTS:
1. Questions should directly test lesson objectives
2. Each question MUST have exactly 4 options (a, b, c, d) with lowercase keys
3. Only ONE correct answer per question
4. Mix of difficulty: Easy (30%), Medium (50%), Hard (20%)
5. Include explanations, shortcut methods, trap analysis, and memory hacks for each question

For EACH question provide:
- question_text, question_type: "mcq", options, correct_answer, explanation (100-200 words), shortcut_method (50-100 words), trap_analysis (50-100 words), memory_hack (30-50 words), marks: 1, difficulty

Return ONLY valid JSON:
{"title": "${lessonName} - Quiz", "description": "Quick assessment quiz", "duration": 15, "total_marks": 10, "passing_marks": 4, "questions": [{"question_text": "Question?", "question_type": "mcq", "options": {"a": "A", "b": "B", "c": "C", "d": "D"}, "correct_answer": "a", "explanation": "Solution", "shortcut_method": "Quick technique", "trap_analysis": "Per-option breakdown", "memory_hack": "Memory tip", "difficulty": "medium", "marks": 1}]}`,
          
          lesson_questions: `Generate 10 HIGH-QUALITY MCQ questions for the lesson "${lessonName}".

Lesson Title: ${lessonName}
Lesson Description: ${lessonDesc}
${context}

CRITICAL REQUIREMENTS:
1. Questions should align with lesson learning objectives
2. Each question MUST have exactly 4 options (a, b, c, d) using lowercase keys
3. Only ONE correct answer per question
4. Plausible distractors based on common student mistakes
5. Mix of difficulty: Easy (30%), Medium (50%), Hard (20%)
6. For numerical questions: use LaTeX for formulas ($formula$), include units

For EACH question provide ALL of these fields:
- question_text: Complete question with all necessary data
- options: {"a": "...", "b": "...", "c": "...", "d": "..."}
- correct_answer: Single lowercase letter (a/b/c/d)
- explanation: Detailed step-by-step solution (100-200 words)
- shortcut_method: Quick solving technique if applicable (50-100 words)
- trap_analysis: Why students might choose each wrong option — per-option breakdown (50-100 words)
- memory_hack: Mnemonic or tip to remember the concept (30-50 words)
- difficulty: "easy", "medium", or "hard"
- marks: 1

Return ONLY valid JSON array (no markdown, no explanation):
[{"question_text": "Question?", "options": {"a": "A", "b": "B", "c": "C", "d": "D"}, "correct_answer": "a", "explanation": "Detailed solution", "shortcut_method": "Quick technique", "trap_analysis": "Per-option breakdown", "memory_hack": "Memory tip", "difficulty": "medium", "marks": 1}]

IMPORTANT: Generate exactly 10 questions. ALL fields (explanation, shortcut_method, trap_analysis, memory_hack) are MANDATORY.`,
          
          syllabus: `Generate the COMPLETE and COMPREHENSIVE syllabus structure for "${subjectName}" for ${examName} preparation.
${context}

CRITICAL REQUIREMENTS:
1. Use the LATEST OFFICIAL SYLLABUS from the examining body (2024-2025)
2. Include ALL topics and sub-topics — completeness is critical
3. DO NOT miss any topic from the official syllabus
4. Organize topics in logical learning order (foundational concepts first)
5. Depth level: detailed

For each topic provide:
- name: Official topic name as per syllabus
- description: Detailed description (50-100 words) covering key concepts, important formulas/theorems, and common question types
- order: Sequential order number
- subtopics: Array of subtopics with learning objectives
- estimated_hours: Realistic study hours factoring in theory + practice

Return ONLY valid JSON array (no markdown, no explanation):
[{"name": "Official Topic Name", "description": "Detailed description covering key concepts, formulas, and exam focus areas", "order": 1, "subtopics": [{"name": "Subtopic", "description": "Description", "learning_objectives": ["Objective 1", "Objective 2"]}], "estimated_hours": 5, "importance": "high"}]

IMPORTANT: Include EVERY topic from the official syllabus. Use official terminology.`,
          
          full_syllabus: `Generate the COMPLETE and COMPREHENSIVE syllabus structure for the "${examName}" examination.
${context}

CRITICAL REQUIREMENTS:
1. Use the LATEST OFFICIAL SYLLABUS from the examining body (2024-2025)
2. Include ALL subjects and their topics — completeness is critical
3. DO NOT miss any subject or topic from the official syllabus
4. Organize in logical learning order (foundational subjects first)
5. Depth level: comprehensive

For each subject provide:
- name: Official subject name as per syllabus
- description: Subject description for the exam
- importance: "high", "medium", or "low" based on exam weightage
- estimated_hours: Realistic total study hours
- topics: Array of topics with subtopics

Return ONLY valid JSON array (no markdown, no explanation):
[{"name": "Subject Name", "description": "Subject description", "importance": "high", "estimated_hours": 100, "topics": [{"name": "Topic", "subtopics": ["Subtopic 1", "Subtopic 2"]}]}]

IMPORTANT: Include EVERY subject from the official syllabus. Use official terminology.`,
          
          notes: `Create CONCISE but COMPREHENSIVE revision notes for: "${topicName}"
Style: detailed
Include helpful mnemonics and memory aids for every key concept.
${context}

Structure the notes with these sections:

1. **Key Concepts at a Glance**
   - One-line definitions of important terms
   - Core principles in bullet points

2. **Formula Sheet** (use LaTeX: $formula$)
   - ALL important formulas for this topic
   - Conditions/limitations for each formula
   - Units and dimensions

3. **Important Values & Constants**
   - Standard values to remember
   - Commonly used data in problems

4. **Quick Facts**
   - Must-know facts for the exam
   - Frequently tested points

5. **Memory Aids**
   - Mnemonics for formulas
   - Tricks to remember sequences/lists
   - Visual memory techniques

6. **Previous Year Insights**
   - Common question patterns
   - Frequently asked concepts

7. **Last-Minute Revision Checklist**
   - 10-15 most important points
   - Common mistakes to avoid

Keep it CONCISE — this is for quick revision, not detailed study.

Return ONLY valid JSON object (no markdown, no explanation):
{"title": "Notes title", "content": "Complete notes content in clean Markdown with all 7 sections", "key_concepts": ["Concept 1", "Concept 2"], "mnemonics": ["Mnemonic 1"], "diagram_suggestions": [{"type": "flowchart", "description": "Description"}]}`,
          
          subject_notes: `Create CONCISE but COMPREHENSIVE revision notes for: "${subjectName}"
Style: detailed
Include helpful mnemonics and memory aids for every key concept.
${context}

Structure the notes with these sections:

1. **Key Concepts at a Glance** — One-line definitions, core principles
2. **Formula Sheet** (use LaTeX: $formula$) — ALL important formulas, conditions, units
3. **Important Values & Constants** — Standard values, commonly used data
4. **Quick Facts** — Must-know facts, frequently tested points
5. **Memory Aids** — Mnemonics, tricks, visual memory techniques
6. **Previous Year Insights** — Common question patterns, frequently asked concepts
7. **Last-Minute Revision Checklist** — 10-15 most important points, common mistakes to avoid

Keep it CONCISE — this is for quick revision, not detailed study.

Return ONLY valid JSON object (no markdown, no explanation):
{"title": "Notes title", "content": "Complete notes content in clean Markdown with all 7 sections", "key_concepts": ["Concept 1", "Concept 2"], "mnemonics": ["Mnemonic 1"], "diagram_suggestions": [{"type": "flowchart", "description": "Description"}]}`,
          
          lesson_notes: `Create CONCISE but COMPREHENSIVE revision notes for the lesson: "${lessonName}"
Style: concise
Include helpful mnemonics and memory aids for every key concept.
${context}

Structure the notes with these sections:

1. **Key Concepts at a Glance** — One-line definitions, core principles
2. **Formula Sheet** (use LaTeX: $formula$) — Important formulas, conditions, units
3. **Quick Facts** — Must-know facts, frequently tested points
4. **Memory Aids** — Mnemonics, tricks, visual memory techniques
5. **Last-Minute Revision Checklist** — Most important points, common mistakes to avoid

Keep it CONCISE — this is for quick revision, not detailed study.

Return ONLY valid JSON object (no markdown, no explanation):
{"title": "Notes title", "content": "Complete notes content in clean Markdown", "key_concepts": ["Concept 1", "Concept 2"], "mnemonics": ["Mnemonic 1"]}`,
          
          lesson_content: `Create a COMPREHENSIVE, exam-focused lesson for: "${lessonName}"
Content depth: comprehensive
${context}

Structure the lesson with ALL of these sections:

1. **Introduction** (100-150 words)
   - Real-world applications and relevance
   - Why this topic is important for the exam
   - Prerequisites

2. **Core Concepts** (500-800 words)
   - Clear explanations of all fundamental concepts
   - Step-by-step breakdowns of complex ideas
   - Visual descriptions (describe diagrams/graphs that would help)
   - Connections to related topics

3. **Important Formulas & Theorems** (use LaTeX: $formula$)
   - All relevant formulas with derivations where helpful
   - Conditions for applicability
   - Units and dimensions

4. **Solved Examples** (3-5 examples)
   - Start with basic, progress to exam-level difficulty
   - Complete step-by-step solutions
   - Show both standard method AND shortcut where applicable

5. **Memory Techniques**
   - Mnemonics for formulas and concepts
   - Visual memory aids and quick recall tricks

6. **Common Mistakes & Misconceptions**
   - Typical errors students make
   - How to avoid them

7. **Exam Tips**
   - Question patterns for this topic
   - Time-saving shortcuts

8. **Quick Revision Points**
   - Bullet-point summary of key concepts
   - Formula sheet for this topic

Return ONLY valid JSON object (no markdown, no explanation):
{"title": "${lessonName}", "content": "Complete lesson content in Markdown format with all 8 sections", "key_points": ["Key point 1", "Key point 2"], "examples": [{"title": "Example 1", "content": "Example content"}], "estimated_duration": 30, "difficulty_level": "intermediate"}`,
          
          exam_overview: `You are an expert exam preparation guide writer. Write a comprehensive overview of the "${examName}" examination.
${context}

Requirements:
- Exam pattern and structure
- Marking scheme and weightage
- Important topics and their priority
- Preparation strategy and timeline
- Tips for success
- Common mistakes to avoid

Use markdown formatting with clear sections and bullet points.`,
          
          test_series: `You are an expert test series designer. Design a complete test series plan for "${examName}" preparation.
${context}

Requirements:
- Include sectional tests for each subject
- Full-length mock tests schedule
- Topic-wise tests for weak areas
- Revision tests before exam
- Suggested timeline and frequency
- Performance tracking suggestions

Use markdown with tables for the schedule and clear sections for different test types.`,

          pyq_exam: `Generate 25-30 questions that are representative of actual questions asked in "${examName}" over the past 25 years (2000-2024). These should be realistic previous year question (PYQ) style questions.
${context}

CRITICAL REQUIREMENTS:
1. Include questions from different years spanning 2000-2024
2. Cover all major subjects/sections of the exam
3. Include the year when each question was asked (or likely asked based on pattern)
4. Mix of difficulty levels as per actual exam pattern
5. Questions should reflect the actual exam pattern and style
6. Each question MUST have exactly 4 options (a, b, c, d) with lowercase keys
7. Include detailed explanations, shortcut methods, trap analysis, and memory hacks

For EACH question provide ALL of these fields:
- question_text, options (a,b,c,d), correct_answer (lowercase letter)
- explanation: Detailed step-by-step solution (100-200 words)
- shortcut_method: Quick solving technique (50-100 words)
- trap_analysis: Per-option breakdown of wrong answer traps (50-100 words)
- memory_hack: Mnemonic or tip to remember the concept (30-50 words)
- difficulty, year_asked, subject, marks: 1

Return ONLY a valid JSON array (no markdown, no explanation):
[{"question_text": "Question exactly as it would appear in the exam?", "options": {"a": "Option A", "b": "Option B", "c": "Option C", "d": "Option D"}, "correct_answer": "a", "explanation": "Detailed explanation", "shortcut_method": "Quick solving technique", "trap_analysis": "Per-option breakdown", "memory_hack": "Memory tip", "difficulty": "medium", "year_asked": "2019", "subject": "Subject Name", "marks": 1}]

IMPORTANT: ALL fields (explanation, shortcut_method, trap_analysis, memory_hack) are MANDATORY.`,

          pyq_subject: `Generate 20 questions from "${subjectName}" that are representative of actual questions asked in "${examName}" over the past 25 years (2000-2024).
${context}

CRITICAL REQUIREMENTS:
1. Include questions from different years spanning 2000-2024
2. Cover all major topics within this subject
3. Include the year when each question was asked
4. Mix of difficulty levels as per actual exam pattern
5. Each question MUST have exactly 4 options (a, b, c, d) with lowercase keys
6. Include detailed explanations, shortcut methods, trap analysis, and memory hacks

For EACH question provide ALL of these fields:
- question_text, options (a,b,c,d), correct_answer (lowercase letter)
- explanation: Detailed step-by-step solution (100-200 words)
- shortcut_method: Quick solving technique (50-100 words)
- trap_analysis: Per-option breakdown of wrong answer traps (50-100 words)
- memory_hack: Mnemonic or tip to remember the concept (30-50 words)
- difficulty, year_asked, topic, marks: 1

Return ONLY a valid JSON array (no markdown, no explanation):
[{"question_text": "Question exactly as it would appear in the exam?", "options": {"a": "Option A", "b": "Option B", "c": "Option C", "d": "Option D"}, "correct_answer": "a", "explanation": "Detailed explanation", "shortcut_method": "Quick solving technique", "trap_analysis": "Per-option breakdown", "memory_hack": "Memory tip", "difficulty": "medium", "year_asked": "2018", "topic": "Topic Name", "marks": 1}]

IMPORTANT: ALL fields (explanation, shortcut_method, trap_analysis, memory_hack) are MANDATORY.`,

          pyq_topic: `Generate 15 questions on "${topicName}" that are representative of actual questions asked in "${examName}" over the past 25 years (2000-2024).
${context}

CRITICAL REQUIREMENTS:
1. Include questions from different years spanning 2000-2024
2. Focus specifically on this topic and its subtopics
3. Include the year when each question was asked
4. Mix of difficulty levels as per actual exam pattern
5. Each question MUST have exactly 4 options (a, b, c, d) with lowercase keys
6. Include detailed explanations, shortcut methods, trap analysis, and memory hacks

For EACH question provide ALL of these fields:
- question_text, options (a,b,c,d), correct_answer (lowercase letter)
- explanation: Detailed step-by-step solution (100-200 words)
- shortcut_method: Quick solving technique (50-100 words)
- trap_analysis: Per-option breakdown of wrong answer traps (50-100 words)
- memory_hack: Mnemonic or tip to remember the concept (30-50 words)
- difficulty, year_asked, marks: 1

Return ONLY a valid JSON array (no markdown, no explanation):
[{"question_text": "Question exactly as it would appear in the exam?", "options": {"a": "Option A", "b": "Option B", "c": "Option C", "d": "Option D"}, "correct_answer": "a", "explanation": "Detailed explanation", "shortcut_method": "Quick solving technique", "trap_analysis": "Per-option breakdown", "memory_hack": "Memory tip", "difficulty": "medium", "year_asked": "2021", "marks": 1}]

IMPORTANT: ALL fields (explanation, shortcut_method, trap_analysis, memory_hack) are MANDATORY.`,

          full_mock_test: `Create a COMPLETE FULL-LENGTH MOCK TEST for "${examName}" that exactly mirrors the actual exam pattern.
${context}

IMPORTANT: Use the ACTUAL exam pattern for "${examName}":
- Use the EXACT number of questions as in the real exam
- Use the EXACT time duration as in the real exam
- Use the EXACT marking scheme (positive marks, negative marking) as in the real exam
- Include ALL sections/subjects as per the real exam
- Maintain the same difficulty distribution as the actual exam

CRITICAL REQUIREMENTS:
1. This should be a REALISTIC full-length mock test
2. Each question MUST have exactly 4 options (a, b, c, d) with lowercase keys
3. Only ONE correct answer per question
4. Include detailed explanations, shortcut methods, trap analysis, and memory hacks

For EACH question provide ALL of these fields:
- question_text (use $LaTeX$ for math), question_type: "mcq"
- options: {"a": "...", "b": "...", "c": "...", "d": "..."}
- correct_answer: Single lowercase letter (a/b/c/d)
- explanation: Detailed step-by-step solution (100-200 words)
- shortcut_method: Quick solving technique (50-100 words)
- trap_analysis: Per-option breakdown of wrong answer traps (50-100 words)
- memory_hack: Mnemonic or tip to remember the concept (30-50 words)
- marks, difficulty, section

Return ONLY valid JSON (no markdown, no explanation):
{"title": "Full Mock Test - ${examName}", "description": "Complete mock test based on actual ${examName} pattern", "duration": 180, "total_marks": 100, "passing_marks": 40, "negative_marking": 0.25, "sections": [{"name": "Section Name", "questions_count": 10, "marks_per_question": 1}], "instructions": ["Important instruction 1"], "questions": [{"question_text": "Question?", "question_type": "mcq", "options": {"a": "A", "b": "B", "c": "C", "d": "D"}, "correct_answer": "a", "explanation": "Detailed solution", "shortcut_method": "Quick technique", "trap_analysis": "Per-option breakdown", "memory_hack": "Memory tip", "difficulty": "medium", "marks": 1, "section": "Section Name"}]}

IMPORTANT: ALL fields (explanation, shortcut_method, trap_analysis, memory_hack) are MANDATORY.`
        }

        const prompt = (prompts[action.type] || `Generate ${action.type} content for: ${contextBrief}`) + langInstruction
        
        // Map action types to backend AI modes for optimal system prompt selection
        const modeMap = {
          subjects: 'ARCHITECT', topics: 'ARCHITECT', syllabus: 'ARCHITECT', full_syllabus: 'ARCHITECT',
          lessons: 'ARCHITECT',
          questions: 'EXAMINER', subject_questions: 'EXAMINER', lesson_questions: 'EXAMINER',
          test: 'EXAMINER', lesson_test: 'EXAMINER', full_mock_test: 'EXAMINER',
          pyq_exam: 'EXAMINER', pyq_subject: 'EXAMINER', pyq_topic: 'EXAMINER',
          flashcards: 'EXAMINER_FLASHCARDS', lesson_flashcards: 'EXAMINER_FLASHCARDS',
          flashcard_deck: 'EXAMINER_FLASHCARDS', lesson_flashcard_deck: 'EXAMINER_FLASHCARDS',
          notes: 'AUTHOR', subject_notes: 'AUTHOR', lesson_notes: 'AUTHOR',
          lesson_content: 'AUTHOR', exam_overview: 'AUTHOR', test_series: 'AUTHOR'
        }
        const mode = modeMap[action.type] || 'DEFAULT'

        const response = await api.post('/v1/admin/admin-chat', { 
          message: prompt,
          mode: mode,
          session_id: sessionId.value 
        }, { timeout: 120000 })
        
        if (response.data.session_id) sessionId.value = response.data.session_id
        
        const aiResponse = response.data.response
        generatedContent.value = aiResponse
        
        // Determine content type and parse
        const contentTypeMap = {
          subjects: { type: 'subjects', icon: 'book', title: 'Subjects', gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
          topics: { type: 'topics', icon: 'hash', title: 'Topics', gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
          syllabus: { type: 'syllabus', icon: 'list', title: 'Syllabus', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
          full_syllabus: { type: 'syllabus', icon: 'list', title: 'Full Syllabus', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
          lessons: { type: 'lessons', icon: 'file-text', title: 'Lessons', gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' },
          questions: { type: 'questions', icon: 'help-circle', title: 'Questions', gradient: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)' },
          subject_questions: { type: 'questions', icon: 'help-circle', title: 'Questions', gradient: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)' },
          lesson_questions: { type: 'questions', icon: 'help-circle', title: 'Questions', gradient: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)' },
          pyq_exam: { type: 'pyq', icon: 'archive', title: 'Previous Year Questions', gradient: 'linear-gradient(135deg, #f5af19 0%, #f12711 100%)' },
          pyq_subject: { type: 'pyq', icon: 'archive', title: 'Previous Year Questions', gradient: 'linear-gradient(135deg, #f5af19 0%, #f12711 100%)' },
          pyq_topic: { type: 'pyq', icon: 'archive', title: 'Previous Year Questions', gradient: 'linear-gradient(135deg, #f5af19 0%, #f12711 100%)' },
          full_mock_test: { type: 'mock_test', icon: 'award', title: 'Full Mock Test', gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' },
          flashcards: { type: 'flashcards', icon: 'layers', title: 'Flashcards', gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' },
          lesson_flashcards: { type: 'flashcards', icon: 'layers', title: 'Flashcards', gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' },
          flashcard_deck: { type: 'flashcard_deck', icon: 'layers', title: 'Flashcard Deck', gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' },
          lesson_flashcard_deck: { type: 'flashcard_deck', icon: 'layers', title: 'Flashcard Deck', gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' },
          test: { type: 'test', icon: 'clipboard', title: 'Test', gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
          lesson_test: { type: 'test', icon: 'clipboard', title: 'Quiz', gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
          notes: { type: 'notes', icon: 'edit-3', title: 'Notes', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
          subject_notes: { type: 'notes', icon: 'edit-3', title: 'Notes', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
          lesson_notes: { type: 'notes', icon: 'edit-3', title: 'Notes', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
          lesson_content: { type: 'content', icon: 'file-text', title: 'Lesson Content', gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' },
          exam_overview: { type: 'content', icon: 'file-text', title: 'Exam Overview', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
          test_series: { type: 'content', icon: 'layers', title: 'Test Series Plan', gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' }
        }
        
        generatedContentType.value = contentTypeMap[action.type] || { type: 'content', icon: 'file-text', title: 'Content' }
        
        console.log('Action type:', action.type)
        console.log('Content type for parsing:', generatedContentType.value.type)
        console.log('AI Response preview:', aiResponse?.substring(0, 500))
        
        // Parse the response based on content type
        const parsed = parseGeneratedContent(aiResponse, generatedContentType.value.type)
        console.log('Parsed result:', { itemCount: parsed.items?.length, hasMeta: !!parsed.meta })
        
        generatedItems.value = parsed.items || []
        generatedTestMeta.value = parsed.meta || null
        
        // For notes/content types, if the AI returned JSON with a content field,
        // extract the markdown content so it renders properly instead of showing raw JSON
        const cType = generatedContentType.value.type
        if ((cType === 'notes' || cType === 'content') && aiResponse) {
          const extracted = extractMarkdownFromJSON(aiResponse)
          if (extracted) {
            generatedContent.value = extracted
          }
        }
        
        console.log('generatedItems.value set to:', generatedItems.value.length, 'items')
        
      } catch (e) {
        console.error('Generate failed:', e)
        generatedContent.value = `❌ Generation failed: ${e.response?.data?.error?.message || e.message}`
        generatedContentType.value = { type: 'error', icon: 'alert-circle', title: 'Error' }
        toast.error('Failed to generate content. Please try again.')
      } finally {
        generating.value = false
        generatingType.value = ''
      }
    }

    const parseGeneratedContent = (text, contentType) => {
      let items = []
      let meta = null
      
      console.log('parseGeneratedContent called with contentType:', contentType)
      console.log('Text length:', text?.length)
      
      if (!text || typeof text !== 'string') {
        console.log('Invalid text input')
        return { items: [], meta: null }
      }
      
      // Try JSON parsing first - multiple patterns
      const jsonParsed = tryExtractJSON(text)
      if (jsonParsed) {
        console.log('JSON parsed successfully:', typeof jsonParsed, Array.isArray(jsonParsed) ? jsonParsed.length + ' items' : 'object')
        
        if (Array.isArray(jsonParsed)) {
          items = jsonParsed
        } else if (jsonParsed.questions && Array.isArray(jsonParsed.questions)) {
          items = jsonParsed.questions
          meta = { 
            duration: jsonParsed.duration || jsonParsed.time_limit, 
            total_marks: jsonParsed.total_marks || jsonParsed.marks, 
            total_questions: jsonParsed.questions?.length,
            title: jsonParsed.title,
            description: jsonParsed.description,
            passing_marks: jsonParsed.passing_marks,
            negative_marking: jsonParsed.negative_marking,
            sections: jsonParsed.sections,
            instructions: jsonParsed.instructions
          }
        } else if (jsonParsed.subjects && Array.isArray(jsonParsed.subjects)) {
          items = jsonParsed.subjects
        } else if (jsonParsed.topics && Array.isArray(jsonParsed.topics)) {
          items = jsonParsed.topics
        } else if (jsonParsed.lessons && Array.isArray(jsonParsed.lessons)) {
          items = jsonParsed.lessons
        } else if (jsonParsed.flashcards && Array.isArray(jsonParsed.flashcards)) {
          items = jsonParsed.flashcards
        } else if (jsonParsed.cards && Array.isArray(jsonParsed.cards)) {
          items = jsonParsed.cards
          if (jsonParsed.deck_title || jsonParsed.deck_description) {
            meta = {
              deck_title: jsonParsed.deck_title,
              deck_description: jsonParsed.deck_description
            }
          }
        } else if (jsonParsed.syllabus && Array.isArray(jsonParsed.syllabus)) {
          items = jsonParsed.syllabus
        } else if (jsonParsed.name || jsonParsed.title || jsonParsed.question_text || jsonParsed.front) {
          // Single item wrapped in object
          items = [jsonParsed]
        }
        
        if (items.length > 0) {
          console.log('Returning', items.length, 'items from JSON parsing')
          return { items, meta }
        }
      }
      
      // Fallback to text parsing
      console.log('Falling back to text parsing for contentType:', contentType)
      switch (contentType) {
        case 'questions':
        case 'test':
        case 'pyq':
        case 'mock_test':
          items = parseQuestionsFromText(text)
          break
        case 'subjects':
          items = parseSubjectsFromText(text)
          break
        case 'topics':
        case 'syllabus':
          items = parseTopicsFromText(text)
          break
        case 'lessons':
          items = parseLessonsFromText(text)
          break
        case 'flashcards':
          items = parseFlashcardsFromText(text)
          break
        case 'flashcard_deck':
          items = parseFlashcardsFromText(text)
          break
        case 'notes':
        case 'content':
          // For notes/content, don't parse - show as markdown
          items = []
          break
        default:
          items = []
      }
      
      console.log('Text parsing returned', items.length, 'items')
      return { items, meta }
    }

    // Helper to extract JSON from text with multiple patterns
    const tryExtractJSON = (text) => {
      if (!text || typeof text !== 'string') return null
      
      // Pattern 1: ```json ... ``` (most common AI response format)
      let match = text.match(/```json\s*([\s\S]*?)```/i)
      if (match) {
        try { 
          const parsed = JSON.parse(match[1].trim())
          console.log('JSON extracted from ```json block:', parsed)
          return parsed 
        } catch (e) { 
          console.log('Failed to parse ```json block:', e.message)
        }
      }
      
      // Pattern 2: ``` ... ``` (code block without language)
      match = text.match(/```\s*([\s\S]*?)```/)
      if (match && (match[1].trim().startsWith('[') || match[1].trim().startsWith('{'))) {
        try { 
          const parsed = JSON.parse(match[1].trim())
          console.log('JSON extracted from code block:', parsed)
          return parsed 
        } catch (e) { 
          console.log('Failed to parse code block:', e.message)
        }
      }
      
      // Pattern 3: Find JSON array by bracket matching
      const arrayStart = text.indexOf('[')
      if (arrayStart !== -1) {
        let depth = 0
        let inString = false
        let escape = false
        let end = -1
        
        for (let i = arrayStart; i < text.length; i++) {
          const char = text[i]
          
          if (escape) { escape = false; continue }
          if (char === '\\') { escape = true; continue }
          if (char === '"' && !escape) { inString = !inString; continue }
          if (inString) continue
          
          if (char === '[') depth++
          if (char === ']') depth--
          
          if (depth === 0) { end = i + 1; break }
        }
        
        if (end > arrayStart) {
          try { 
            const jsonStr = text.substring(arrayStart, end)
            const parsed = JSON.parse(jsonStr)
            if (Array.isArray(parsed) && parsed.length > 0) {
              console.log('JSON array extracted by bracket matching:', parsed.length, 'items')
              return parsed
            }
          } catch (e) { 
            console.log('Failed to parse array by bracket matching:', e.message)
          }
        }
      }
      
      // Pattern 4: Find JSON object by bracket matching
      const objStart = text.indexOf('{')
      if (objStart !== -1) {
        let depth = 0
        let inString = false
        let escape = false
        let end = -1
        
        for (let i = objStart; i < text.length; i++) {
          const char = text[i]
          
          if (escape) { escape = false; continue }
          if (char === '\\') { escape = true; continue }
          if (char === '"' && !escape) { inString = !inString; continue }
          if (inString) continue
          
          if (char === '{') depth++
          if (char === '}') depth--
          
          if (depth === 0) { end = i + 1; break }
        }
        
        if (end > objStart) {
          try { 
            const jsonStr = text.substring(objStart, end)
            const parsed = JSON.parse(jsonStr)
            console.log('JSON object extracted by bracket matching:', parsed)
            return parsed
          } catch (e) { 
            console.log('Failed to parse object by bracket matching:', e.message)
          }
        }
      }
      
      console.log('No JSON found in text')
      return null
    }

    const clearGeneratedContent = () => {
      generatedContent.value = ''
      generatedItems.value = []
      generatedContentType.value = null
      generatedTestMeta.value = null
      flippedCards.value = []
    }

    // Extract markdown content from a JSON response string
    // Handles cases where AI returns {"title": "...", "content": "...markdown..."} instead of plain markdown
    const extractMarkdownFromJSON = (text) => {
      if (!text || typeof text !== 'string') return null
      const trimmed = text.trim()
      
      // Only process if it looks like JSON
      if (!trimmed.startsWith('{') && !trimmed.startsWith('[') && !trimmed.includes('```json')) return null

      // Try to parse via tryExtractJSON first
      const parsed = tryExtractJSON(trimmed)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const content = parsed.content || parsed.notes || parsed.text || parsed.body
        if (content && typeof content === 'string' && content.length > 50) {
          return content
        }
      }

      // If JSON.parse failed (complex LaTeX escaping etc.), try regex extraction
      const contentMatch = trimmed.match(/"content"\s*:\s*"([\s\S]*?)"\s*,\s*"(?:key_|examples|mnemonics|estimated|difficulty|tags|summary|key_points|diagram)/)
      if (contentMatch) {
        try {
          return JSON.parse('"' + contentMatch[1] + '"')
        } catch (e) {
          return contentMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"').replace(/\\\\/g, '\\')
        }
      }

      return null
    }

    // Generate mode select change handlers (must be after clearGeneratedContent)
    const onExamChange = async () => {
      clearGeneratedContent()
      await loadSubjects()
    }

    const onSubjectChange = async () => {
      clearGeneratedContent()
      await loadTopics()
    }

    const onTopicChange = async () => {
      clearGeneratedContent()
      await loadLessons()
    }

    const onLessonChange = () => {
      clearGeneratedContent()
    }

    const toggleFlashcard = (index) => {
      const idx = flippedCards.value.indexOf(index)
      if (idx > -1) {
        flippedCards.value.splice(idx, 1)
      } else {
        flippedCards.value.push(index)
      }
    }

    const copyGeneratedContent = async () => {
      try {
        await navigator.clipboard.writeText(generatedContent.value)
        addSystemMessage('✅ Content copied to clipboard!')
      } catch (e) {
        console.error('Copy failed:', e)
      }
    }

    const regenerateContent = () => {
      if (lastGenerateAction.value) {
        generateContentInline(lastGenerateAction.value)
      }
    }

    const openSaveModalFromGenerated = async () => {
      // For notes/content, we don't need generatedItems - we save the raw content
      const isNotesOrContent = generatedContentType.value?.type === 'notes' || generatedContentType.value?.type === 'content'
      
      if (!isNotesOrContent && !generatedItems.value.length) return
      if (!generatedContentType.value) return
      
      const typeMapping = {
        subjects: 'subjects',
        topics: 'topics',
        lessons: 'lessons',
        questions: 'questions',
        test: 'questions',
        flashcards: 'flashcards',
        flashcard_deck: 'flashcards',
        syllabus: 'topics',
        notes: 'notes',
        content: 'content'
      }
      
      const saveType = typeMapping[generatedContentType.value.type] || generatedContentType.value.type
      
      // Pre-populate saveTarget from current Generate mode selections
      saveTarget.examId = selectedExam.value || ''
      saveTarget.subjectId = selectedSubject.value || ''
      saveTarget.topicId = selectedTopic.value || ''
      saveTarget.lessonId = selectedLesson.value || ''
      saveTarget.testId = ''
      
      // Pre-load dependent data based on current selections
      if (saveTarget.examId) {
        try {
          const res = await api.get(`/v1/admin/exams/${saveTarget.examId}/subjects`)
          saveSubjects.value = res.data.subjects || res.data || []
        } catch (e) { saveSubjects.value = [] }
      }
      
      if (saveTarget.subjectId) {
        try {
          const res = await api.get(`/v1/admin/subjects/${saveTarget.subjectId}/topics`)
          saveTopics.value = res.data.topics || res.data || []
        } catch (e) { saveTopics.value = [] }
      }
      
      if (saveTarget.topicId) {
        try {
          const [lessonsRes, testsRes] = await Promise.all([
            api.get(`/v1/admin/topics/${saveTarget.topicId}/lessons`),
            api.get(`/v1/admin/topics/${saveTarget.topicId}/tests`).catch(() => ({ data: [] }))
          ])
          saveLessons.value = lessonsRes.data.lessons || lessonsRes.data || []
          saveTests.value = testsRes.data.tests || testsRes.data || []
        } catch (e) { 
          saveLessons.value = []
          saveTests.value = []
        }
      }
      
      // For notes/content, pass a dummy array with one item for the preview
      const itemsToPass = isNotesOrContent 
        ? [{ title: 'Generated Notes', content: generatedContent.value }]
        : generatedItems.value
      
      openSaveModal(saveType, itemsToPass, generatedContent.value)
    }

    // ==================== RETURN ====================
    return {
      // State
      isOpen, isExpanded, activeMode, messages, inputMessage, loading, loadingText,
      sessionId, aiStatus, chatContainer, inputField, fileInput, attachments,
      showCreateModal, activeCreateCard, createForm, creatingWithAI,
      showSaveModal, saveModalConfig, parsedContent, selectedItems, previewLimit,
      savingContent, saveTarget, saveSubjects, saveTopics, saveLessons, saveTests,
      exams, subjects, topics, lessons, selectedExam, selectedSubject, selectedTopic, selectedLesson,
      selectedLanguage, LANGUAGE_OPTIONS,
      quickActions, createCards,
      filteredSubjects, filteredTopics, filteredLessons,
      // Generate inline state
      generating, generatingType, generatingText, generatedContent, generatedItems,
      generatedContentType, generatedTestMeta, flippedCards,
      
      // Computed
      canSubmitCreate, currentContextPath, generateContextHint, availableGenerateActions, canSaveContent,
      
      // Methods
      toggleChat, toggleExpanded, toggleMode, triggerFileInput, handleFileSelect, removeAttachment,
      sendMessage, sendQuickAction, clearChat, copyMessage, renderMarkdown, escapeHtml, formatTime, autoResize,
      openCreateModal, closeCreateModal, getFieldOptions, handleSelectChange, aiAssistCreate, submitCreate,
      loadSubjects, loadTopics, loadLessons, generateContent, executeAction,
      openSaveModal, closeSaveModal, onSaveExamChange, onSaveSubjectChange, onSaveTopicChange,
      selectAllItems, getPreviewTitle, truncateText, saveContent,
      // Generate inline methods
      generateContentInline, clearGeneratedContent, toggleFlashcard, copyGeneratedContent,
      regenerateContent, openSaveModalFromGenerated,
      // Generate mode select handlers
      onExamChange, onSubjectChange, onTopicChange, onLessonChange
    }
  }
}
</script>


<style scoped>
.ai-chatbox-wrapper {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  max-height: calc(100vh - 48px);
}

/* ===== FLOATING ACTION BUTTON ===== */
.ai-fab {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #003049 0%, #005f73 100%);
  border: none;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(0, 48, 73, 0.4);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  flex-shrink: 0;
}

.ai-fab:hover { transform: scale(1.1); box-shadow: 0 6px 28px rgba(0, 48, 73, 0.5); }
.ai-fab.is-open { background: linear-gradient(135deg, #dc3545 0%, #c82333 100%); }
.ai-fab.has-messages { animation: fab-pulse 2s infinite; }

.fab-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: #fcbf49;
  color: #003049;
  font-size: 10px;
  font-weight: 700;
  padding: 3px 6px;
  border-radius: 6px;
}

.fab-ring {
  position: absolute;
  inset: -4px;
  border: 2px solid rgba(0, 48, 73, 0.3);
  border-radius: 50%;
  animation: ring-pulse 2s infinite;
}

@keyframes fab-pulse {
  0%, 100% { box-shadow: 0 4px 20px rgba(0, 48, 73, 0.4); }
  50% { box-shadow: 0 4px 30px rgba(0, 48, 73, 0.6), 0 0 0 8px rgba(0, 48, 73, 0.1); }
}

@keyframes ring-pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.2); opacity: 0; }
}

/* ===== CHAT WINDOW ===== */
.ai-chatbox {
  width: 420px;
  height: 560px;
  max-height: calc(100vh - 120px);
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin-bottom: 16px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.ai-chatbox.expanded { height: calc(100vh - 120px); width: 520px; }

/* ===== HEADER ===== */
.chatbox-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: linear-gradient(135deg, #003049 0%, #005f73 100%);
  color: #fff;
  flex-shrink: 0;
}

.header-left { display: flex; align-items: center; gap: 10px; }
.header-icon { display: flex; align-items: center; justify-content: center; transition: all 0.3s ease; }
.header-icon.thinking { animation: icon-thinking 1s ease-in-out infinite; }
.header-title { font-weight: 600; font-size: 15px; }

.status-badge {
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 10px;
  text-transform: capitalize;
}
.status-badge.online { background: #10b981; }
.status-badge.pulse { animation: status-pulse 1.5s ease-in-out infinite; background: #fcbf49; color: #003049; }

.header-actions { display: flex; gap: 4px; }
.header-btn {
  background: rgba(255,255,255,0.1);
  border: none;
  color: #fff;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}
.header-btn:hover { background: rgba(255,255,255,0.2); }

/* ===== MODE TABS ===== */
.mode-tabs {
  display: flex;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  flex-shrink: 0;
}

.mode-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px;
  background: none;
  border: none;
  font-size: 12px;
  font-weight: 500;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 2px solid transparent;
}

.mode-tab:hover { color: #003049; background: rgba(0, 48, 73, 0.05); }
.mode-tab.active { color: #003049; border-bottom-color: #003049; background: #fff; }

/* ===== CREATE MODE ===== */
.create-mode, .generate-mode {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background: linear-gradient(180deg, #f8f9fa 0%, #fff 100%);
}

.create-header, .generate-header {
  text-align: center;
  margin-bottom: 16px;
}

.create-header h4, .generate-header h4 {
  margin: 0 0 4px;
  color: #003049;
  font-size: 16px;
}

.create-header p, .generate-header p {
  margin: 0;
  font-size: 12px;
  color: #6c757d;
}

.create-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.create-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.create-card:hover {
  border-color: #003049;
  box-shadow: 0 4px 12px rgba(0, 48, 73, 0.1);
  transform: translateX(4px);
}

.card-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
}

.card-info { flex: 1; }
.card-title { display: block; font-weight: 600; font-size: 14px; color: #212529; }
.card-desc { display: block; font-size: 11px; color: #6c757d; margin-top: 2px; }
.card-arrow { color: #adb5bd; transition: transform 0.2s; }
.create-card:hover .card-arrow { transform: translateX(4px); color: #003049; }

/* ===== GENERATE MODE ===== */
.context-selector {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 16px;
}

.context-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.context-row:last-child { margin-bottom: 0; }
.context-row label { font-size: 12px; font-weight: 500; color: #495057; width: 60px; flex-shrink: 0; }

.context-select {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 13px;
  background: #fff;
  cursor: pointer;
}

.context-select:focus { border-color: #003049; outline: none; }

.context-display {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: linear-gradient(135deg, rgba(0, 48, 73, 0.05) 0%, rgba(0, 95, 115, 0.05) 100%);
  border-radius: 8px;
  margin-bottom: 12px;
}

.context-label { font-size: 11px; color: #6c757d; font-weight: 500; }
.context-path { font-size: 12px; color: #003049; font-weight: 600; }

.generate-actions-section { margin-top: 8px; }
.actions-label {
  font-size: 11px;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.generate-actions {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.generate-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px 10px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  color: #003049;
  cursor: pointer;
  transition: all 0.2s ease;
}

.generate-btn:hover:not(:disabled) {
  background: #003049;
  color: #fff;
  border-color: #003049;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 48, 73, 0.2);
}

.generate-btn.primary {
  background: linear-gradient(135deg, #003049 0%, #005f73 100%);
  color: #fff;
  border-color: transparent;
}

.generate-btn.primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 48, 73, 0.3);
}

.no-selection {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #adb5bd;
  text-align: center;
}

.no-selection p { margin: 12px 0 0; font-size: 13px; }

/* ===== GENERATE LOADING ===== */
.generate-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 20px;
  margin-top: 16px;
  background: linear-gradient(135deg, rgba(0, 48, 73, 0.03) 0%, rgba(0, 95, 115, 0.03) 100%);
  border-radius: 12px;
}

.loading-spinner {
  color: #003049;
  margin-bottom: 12px;
}

.loading-text {
  font-size: 14px;
  font-weight: 500;
  color: #003049;
  margin-bottom: 8px;
}

.loading-dots {
  display: flex;
  gap: 4px;
}

.loading-dots span {
  width: 8px;
  height: 8px;
  background: #003049;
  border-radius: 50%;
  animation: loading-dot 1.4s ease-in-out infinite;
}

.loading-dots span:nth-child(2) { animation-delay: 0.2s; }
.loading-dots span:nth-child(3) { animation-delay: 0.4s; }

@keyframes loading-dot {
  0%, 60%, 100% { transform: scale(0.6); opacity: 0.4; }
  30% { transform: scale(1); opacity: 1; }
}

/* ===== GENERATED CONTENT SECTION ===== */
.generated-content-section {
  margin-top: 16px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
}

.generated-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: linear-gradient(135deg, rgba(0, 48, 73, 0.05) 0%, rgba(0, 95, 115, 0.05) 100%);
  border-bottom: 1px solid #e9ecef;
}

.generated-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #003049;
}

.generated-count {
  font-size: 11px;
  font-weight: 500;
  background: #003049;
  color: #fff;
  padding: 2px 8px;
  border-radius: 10px;
}

.generated-actions {
  display: flex;
  gap: 4px;
}

.gen-action-btn {
  background: none;
  border: none;
  color: #6c757d;
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.gen-action-btn:hover {
  background: #e9ecef;
  color: #003049;
}

/* ===== CONTENT LISTS ===== */
.content-list {
  max-height: 300px;
  overflow-y: auto;
  padding: 8px;
}

.content-item {
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 8px;
  transition: all 0.2s ease;
}

.content-item:last-child { margin-bottom: 0; }
.content-item:hover { background: #f0f4f8; }

.item-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.item-number {
  width: 24px;
  height: 24px;
  background: #003049;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
}

.item-name {
  font-size: 13px;
  font-weight: 600;
  color: #212529;
  flex: 1;
}

.item-desc {
  font-size: 12px;
  color: #6c757d;
  margin: 0 0 6px;
  line-height: 1.4;
}

.item-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 11px;
  color: #6c757d;
}

.item-meta span {
  display: flex;
  align-items: center;
  gap: 4px;
}

.importance-badge {
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: capitalize;
  font-weight: 500;
}

.importance-badge.high { background: #fee2e2; color: #991b1b; }
.importance-badge.medium { background: #fef3c7; color: #92400e; }
.importance-badge.low { background: #d1fae5; color: #065f46; }

.duration-badge {
  font-size: 10px;
  padding: 2px 6px;
  background: #e0e7ff;
  color: #3730a3;
  border-radius: 4px;
}

.hours-badge {
  font-size: 10px;
  padding: 2px 6px;
  background: #dbeafe;
  color: #1e40af;
  border-radius: 4px;
}

.concepts { font-size: 11px; color: #6c757d; font-style: italic; }

.objectives {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed #e9ecef;
}

.objectives ul {
  margin: 4px 0 0;
  padding-left: 16px;
  font-size: 11px;
  color: #495057;
}

.objectives li { margin-bottom: 2px; }

.objective {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 11px;
  color: #059669;
  margin-bottom: 4px;
}

/* ===== QUESTIONS LIST ===== */
.question-item {
  background: #fff;
  border: 1px solid #e9ecef;
  padding: 14px;
}

.question-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.q-number {
  background: linear-gradient(135deg, #003049 0%, #005f73 100%);
  color: #fff;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

.marks-badge {
  font-size: 10px;
  padding: 2px 6px;
  background: #dbeafe;
  color: #1e40af;
  border-radius: 4px;
  margin-left: auto;
}

.question-text {
  font-size: 13px;
  color: #212529;
  margin: 0 0 12px;
  line-height: 1.5;
}

.options-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.option {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 10px;
  background: #f8f9fa;
  border-radius: 6px;
  font-size: 12px;
  transition: all 0.2s ease;
}

.option.correct {
  background: #d1fae5;
  border: 1px solid #10b981;
}

.opt-key {
  width: 20px;
  height: 20px;
  background: #e9ecef;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  color: #495057;
  flex-shrink: 0;
}

.option.correct .opt-key {
  background: #10b981;
  color: #fff;
}

.opt-text { flex: 1; color: #495057; }
.correct-icon { color: #10b981; flex-shrink: 0; }

.explanation {
  margin-top: 10px;
  padding: 10px;
  background: #fffbeb;
  border-radius: 6px;
  font-size: 12px;
  color: #92400e;
  line-height: 1.4;
}

.explanation strong { color: #78350f; }

.shortcut-method {
  margin-top: 10px;
  padding: 10px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 6px;
  font-size: 12px;
  color: #0369a1;
  line-height: 1.4;
  border-left: 3px solid #0ea5e9;
}

.shortcut-method .shortcut-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  color: #0c4a6e;
}

.shortcut-method .shortcut-header strong { color: #0c4a6e; }

.shortcut-method p {
  margin: 0;
  color: #075985;
}

:global(body.dark-mode) .shortcut-method {
  background: linear-gradient(135deg, #0c4a6e 0%, #164e63 100%);
  border-left-color: #22d3ee;
  color: #a5f3fc;
}

:global(body.dark-mode) .shortcut-method .shortcut-header { color: #67e8f9; }
:global(body.dark-mode) .shortcut-method .shortcut-header strong { color: #67e8f9; }
:global(body.dark-mode) .shortcut-method p { color: #a5f3fc; }

/* ===== FLASHCARDS ===== */
.flashcards-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  padding: 12px;
}

.flashcard-item {
  perspective: 1000px;
  cursor: pointer;
  height: 120px;
}

.flashcard-inner {
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform 0.6s;
  transform-style: preserve-3d;
}

.flashcard-inner.flipped { transform: rotateY(180deg); }

.flashcard-front, .flashcard-back {
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  border-radius: 10px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.flashcard-front {
  background: linear-gradient(135deg, #003049 0%, #005f73 100%);
  color: #fff;
}

.flashcard-back {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #fff;
  transform: rotateY(180deg);
}

.card-number {
  position: absolute;
  top: 8px;
  left: 8px;
  font-size: 10px;
  opacity: 0.7;
}

.flashcard-front p, .flashcard-back p {
  margin: 0;
  font-size: 12px;
  line-height: 1.4;
}

.flip-hint {
  position: absolute;
  bottom: 8px;
  font-size: 9px;
  opacity: 0.6;
}

/* ===== FLASHCARD DECK ===== */
.flashcard-deck-section {
  padding: 0;
  max-height: 350px;
  overflow-y: auto;
}

.deck-meta-header {
  padding: 14px;
  background: linear-gradient(135deg, rgba(250, 112, 154, 0.08) 0%, rgba(254, 225, 64, 0.08) 100%);
  border-bottom: 1px solid #e9ecef;
}

.deck-meta-title {
  margin: 0 0 4px;
  font-size: 15px;
  color: #212529;
}

.deck-meta-desc {
  margin: 0 0 8px;
  font-size: 12px;
  color: #6c757d;
  line-height: 1.4;
}

.deck-meta-stats {
  display: flex;
  gap: 12px;
}

.flashcard-hint {
  position: absolute;
  bottom: 8px;
  font-size: 10px;
  opacity: 0.8;
  max-width: 90%;
  text-align: center;
}

/* ===== SYLLABUS STRUCTURE ===== */
.syllabus-item { background: #fff; border: 1px solid #e9ecef; }

.syllabus-topic {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.subtopics {
  padding-left: 32px;
  margin-top: 8px;
}

.subtopic {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 12px;
  color: #495057;
  margin-bottom: 4px;
}

.sub-bullet { color: #003049; font-weight: bold; }

/* ===== TEST STRUCTURE ===== */
.test-meta {
  display: flex;
  gap: 16px;
  padding: 12px;
  background: linear-gradient(135deg, rgba(0, 48, 73, 0.05) 0%, rgba(0, 95, 115, 0.05) 100%);
  border-bottom: 1px solid #e9ecef;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #003049;
  font-weight: 500;
}

/* ===== CONTENT MARKDOWN ===== */
.content-markdown {
  padding: 16px;
  max-height: 350px;
  overflow-y: auto;
}

.parse-warning {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  margin-bottom: 12px;
  font-size: 12px;
  color: #92400e;
}

/* ===== SAVE ACTIONS BAR ===== */
.save-actions-bar {
  display: flex;
  gap: 8px;
  padding: 12px;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

.save-all-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #003049 0%, #005f73 100%);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.save-all-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 48, 73, 0.3);
}

.regenerate-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  color: #495057;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.regenerate-btn:hover {
  background: #f8f9fa;
  border-color: #003049;
  color: #003049;
}

.generate-btn.active {
  background: linear-gradient(135deg, #003049 0%, #005f73 100%);
  color: #fff;
  border-color: transparent;
}

.generate-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
}

/* ===== QUICK ACTIONS ===== */
.quick-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  flex-shrink: 0;
}

.quick-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 20px;
  font-size: 11px;
  color: #003049;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.quick-btn:hover {
  background: #003049;
  color: #fff;
  border-color: #003049;
  transform: translateY(-2px);
}

/* ===== CHAT BODY ===== */
.chatbox-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background: linear-gradient(180deg, #f8f9fa 0%, #fff 100%);
  min-height: 0;
}

.messages-container { display: flex; flex-direction: column; }

/* ===== WELCOME MESSAGE ===== */
.welcome-message {
  text-align: center;
  padding: 30px 20px;
  color: #6c757d;
}

.welcome-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #003049 0%, #005f73 100%);
  border-radius: 50%;
  color: #fff;
  margin-bottom: 8px;
}

.welcome-message h4 { margin: 12px 0 8px; color: #003049; }
.welcome-message p { font-size: 13px; }

/* ===== MESSAGES ===== */
.message {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  align-items: flex-start;
}

.message.user { flex-direction: row-reverse; }

.message-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.message.user .message-avatar { background: #003049; color: #fff; }
.message.assistant .message-avatar { background: #e9ecef; color: #495057; }

.message-content { max-width: 75%; }

.message-attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 6px;
}

.attachment-chip {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: rgba(0, 48, 73, 0.1);
  border-radius: 12px;
  font-size: 11px;
  color: #003049;
}

.message.user .attachment-chip { background: rgba(255, 255, 255, 0.2); color: #fff; }

.message-text {
  padding: 10px 14px;
  border-radius: 14px;
  font-size: 13px;
  line-height: 1.5;
}

.message.user .message-text {
  background: linear-gradient(135deg, #003049 0%, #005f73 100%);
  color: #fff;
  border-bottom-right-radius: 4px;
}

.message.assistant .message-text {
  background: #fff;
  color: #212529;
  border-bottom-left-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.message-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.msg-action-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border: none;
  border-radius: 6px;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.msg-action-btn.primary {
  background: linear-gradient(135deg, #003049 0%, #005f73 100%);
  color: #fff;
}

.msg-action-btn.secondary {
  background: #e9ecef;
  color: #003049;
}

.msg-action-btn:hover { transform: scale(1.05); box-shadow: 0 2px 8px rgba(0, 48, 73, 0.3); }

.message-time {
  font-size: 10px;
  color: #6c757d;
  margin-top: 4px;
  padding: 0 6px;
}

.copy-btn {
  opacity: 0;
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 4px;
  transition: all 0.2s ease;
  border-radius: 4px;
}

.message:hover .copy-btn { opacity: 1; }
.copy-btn:hover { background: #e9ecef; color: #003049; }

/* ===== THINKING INDICATOR ===== */
.thinking-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.thinking-text { font-size: 13px; color: #6c757d; font-weight: 500; }

.thinking-dots { display: flex; gap: 4px; }
.thinking-dots span {
  width: 6px;
  height: 6px;
  background: linear-gradient(135deg, #003049 0%, #005f73 100%);
  border-radius: 50%;
  animation: thinking-dot 1.4s ease-in-out infinite;
}
.thinking-dots span:nth-child(2) { animation-delay: 0.2s; }
.thinking-dots span:nth-child(3) { animation-delay: 0.4s; }

@keyframes thinking-dot {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30% { transform: translateY(-6px); opacity: 1; }
}

/* ===== ATTACHMENTS PREVIEW ===== */
.attachments-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 12px;
  background: #f0f4f8;
  border-top: 1px solid #e9ecef;
  flex-shrink: 0;
}

.attachment-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 12px;
  color: #003049;
}

.attachment-name {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attachment-remove {
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  padding: 2px;
  display: flex;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.attachment-remove:hover { background: #fee2e2; }

/* ===== FOOTER ===== */
.chatbox-footer {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  padding: 12px;
  background: #fff;
  border-top: 1px solid #e9ecef;
  flex-shrink: 0;
}

.action-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-btn:hover { transform: scale(1.1); }
.clear-btn { color: #dc3545; }
.clear-btn:hover { background: #fee2e2; }
.attach-btn { color: #003049; }
.attach-btn:hover { background: #e9ecef; }
.attach-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.file-input { display: none; }

.input-form {
  flex: 1;
  display: flex;
  gap: 8px;
  align-items: flex-end;
}

.chat-input {
  flex: 1;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 10px 14px;
  font-size: 13px;
  resize: none;
  max-height: 100px;
  outline: none;
  transition: all 0.2s ease;
  font-family: inherit;
}

.chat-input:focus { border-color: #003049; box-shadow: 0 0 0 3px rgba(0, 48, 73, 0.1); }
.chat-input:disabled { background: #f8f9fa; cursor: not-allowed; }

.send-btn {
  background: #e9ecef;
  border: none;
  color: #6c757d;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.send-btn.ready { background: linear-gradient(135deg, #003049 0%, #005f73 100%); color: #fff; }
.send-btn:hover:not(:disabled) { transform: scale(1.1); }
.send-btn:disabled { cursor: not-allowed; }

.send-loader {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: send-spin 0.8s linear infinite;
}

@keyframes send-spin { to { transform: rotate(360deg); } }

/* ===== CREATE MODAL ===== */
.create-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(4px);
}

.create-modal {
  width: 90%;
  max-width: 480px;
  max-height: 90vh;
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.save-modal { max-width: 560px; }

.modal-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px;
  color: #fff;
  position: relative;
}

.modal-header h3 { margin: 0; font-size: 18px; flex: 1; }

.modal-close {
  background: rgba(255,255,255,0.2);
  border: none;
  color: #fff;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.modal-close:hover { background: rgba(255,255,255,0.3); }

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.form-group { margin-bottom: 16px; }

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #495057;
  margin-bottom: 6px;
}

.form-group .required { color: #dc3545; }

.auto-create-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  padding: 8px 10px;
  background: #e0f2fe;
  border: 1px solid #0ea5e9;
  border-radius: 6px;
  font-size: 11px;
  color: #0369a1;
}

.form-input, .form-textarea, .form-select {
  width: 100%;
  padding: 10px 14px;
  border: 2px solid #e9ecef;
  border-radius: 10px;
  font-size: 14px;
  transition: all 0.2s ease;
  box-sizing: border-box;
  font-family: inherit;
}

.form-input:focus, .form-textarea:focus, .form-select:focus {
  border-color: #003049;
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 48, 73, 0.1);
}

.form-textarea { resize: vertical; min-height: 80px; }

.ai-assist-section {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: linear-gradient(135deg, rgba(0, 48, 73, 0.05) 0%, rgba(0, 95, 115, 0.05) 100%);
  border-radius: 10px;
  margin-top: 8px;
}

.ai-assist-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #003049 0%, #005f73 100%);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ai-assist-btn:hover:not(:disabled) { transform: scale(1.05); }
.ai-assist-btn:disabled { opacity: 0.7; cursor: not-allowed; }

.ai-hint { font-size: 12px; color: #6c757d; }

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 20px;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

.btn-cancel, .btn-create {
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-cancel {
  background: #fff;
  border: 1px solid #e9ecef;
  color: #495057;
}

.btn-cancel:hover { background: #f8f9fa; }

.btn-create {
  background: linear-gradient(135deg, #003049 0%, #005f73 100%);
  border: none;
  color: #fff;
}

.btn-create:hover:not(:disabled) { transform: scale(1.02); box-shadow: 0 4px 12px rgba(0, 48, 73, 0.3); }
.btn-create:disabled { opacity: 0.6; cursor: not-allowed; }

/* ===== SAVE MODAL SPECIFIC ===== */
.save-target-section { margin-bottom: 20px; }
.save-target-section h4 { margin: 0 0 12px; font-size: 14px; color: #003049; }

.target-selectors {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.target-selectors .form-group { margin-bottom: 0; }

.content-preview-section { margin-top: 16px; }

.preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.preview-header h4 { margin: 0; font-size: 14px; color: #003049; }
.preview-count { font-size: 12px; color: #6c757d; background: #e9ecef; padding: 2px 8px; border-radius: 10px; }

.notes-preview {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 16px;
  background: #f8f9fa;
}

.notes-preview-content {
  font-size: 13px;
  line-height: 1.6;
  color: #495057;
}

.save-location-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: linear-gradient(135deg, rgba(0, 48, 73, 0.08) 0%, rgba(0, 95, 115, 0.08) 100%);
  border: 1px solid rgba(0, 48, 73, 0.15);
  border-radius: 8px;
  margin-bottom: 12px;
  font-size: 12px;
  color: #003049;
}

.save-location-indicator strong {
  color: #005f73;
}

.notes-preview-content h1,
.notes-preview-content h2,
.notes-preview-content h3 {
  margin-top: 16px;
  margin-bottom: 8px;
  color: #212529;
}

.notes-preview-content p { margin-bottom: 12px; }
.notes-preview-content ul, .notes-preview-content ol { padding-left: 20px; margin-bottom: 12px; }
.notes-preview-content code { background: #e9ecef; padding: 2px 6px; border-radius: 4px; font-size: 12px; }
.notes-preview-content pre { background: #212529; color: #f8f9fa; padding: 12px; border-radius: 8px; overflow-x: auto; }

.preview-list {
  max-height: 250px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 10px;
}

.preview-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid #e9ecef;
  transition: background 0.2s ease;
}

.preview-item:last-child { border-bottom: none; }
.preview-item:hover { background: #f8f9fa; }
.preview-item.selected { background: rgba(0, 48, 73, 0.05); }

.preview-checkbox {
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.preview-checkbox input { opacity: 0; position: absolute; }

.preview-checkbox .checkmark {
  width: 18px;
  height: 18px;
  border: 2px solid #e9ecef;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.preview-checkbox input:checked + .checkmark {
  background: #003049;
  border-color: #003049;
}

.preview-checkbox input:checked + .checkmark::after {
  content: '✓';
  color: #fff;
  font-size: 12px;
}

.preview-content { flex: 1; min-width: 0; }
.preview-title { font-size: 13px; font-weight: 500; color: #212529; }
.preview-subtitle { font-size: 11px; color: #6c757d; margin-top: 2px; }

.difficulty-badge {
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: capitalize;
}

.difficulty-badge.easy { background: #d1fae5; color: #065f46; }
.difficulty-badge.medium { background: #fef3c7; color: #92400e; }
.difficulty-badge.hard { background: #fee2e2; color: #991b1b; }

/* Previous Year Questions Styles */
.pyq-info-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-radius: 8px;
  margin-bottom: 12px;
  font-size: 12px;
  font-weight: 500;
  color: #92400e;
  border: 1px solid #f59e0b;
}

.pyq-item .question-header {
  flex-wrap: wrap;
  gap: 6px;
}

.year-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  padding: 3px 8px;
  background: linear-gradient(135deg, #f5af19 0%, #f12711 100%);
  color: white;
  border-radius: 4px;
  font-weight: 600;
}

.subject-badge, .topic-badge {
  font-size: 10px;
  padding: 2px 6px;
  background: #e0e7ff;
  color: #3730a3;
  border-radius: 4px;
}

.topic-badge {
  background: #dbeafe;
  color: #1e40af;
}

/* Full Mock Test Styles */
.mock-test-header {
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border: 1px solid #10b981;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
}

.mock-test-title {
  font-size: 16px;
  font-weight: 700;
  color: #065f46;
  margin: 0 0 6px 0;
}

.mock-test-desc {
  font-size: 12px;
  color: #047857;
  margin: 0 0 12px 0;
}

.mock-test-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}

.mock-test-meta .meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #065f46;
  background: white;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid #a7f3d0;
}

.mock-test-meta .meta-item.negative {
  background: #fef2f2;
  color: #991b1b;
  border-color: #fecaca;
}

.sections-breakdown {
  margin-bottom: 12px;
}

.sections-breakdown strong {
  font-size: 12px;
  color: #065f46;
  display: block;
  margin-bottom: 6px;
}

.sections-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.section-chip {
  font-size: 11px;
  padding: 4px 8px;
  background: white;
  color: #047857;
  border-radius: 4px;
  border: 1px solid #a7f3d0;
}

.section-badge {
  font-size: 10px;
  padding: 2px 6px;
  background: #ecfdf5;
  color: #065f46;
  border-radius: 4px;
  font-weight: 500;
}

.instructions-box {
  background: white;
  border-radius: 8px;
  padding: 10px;
  border: 1px solid #a7f3d0;
}

.instructions-box strong {
  font-size: 12px;
  color: #065f46;
  display: block;
  margin-bottom: 6px;
}

.instructions-box ul {
  margin: 0;
  padding-left: 18px;
}

.instructions-box li {
  font-size: 11px;
  color: #047857;
  margin-bottom: 4px;
}

.mock-question .question-header {
  flex-wrap: wrap;
  gap: 6px;
}

.preview-more { padding: 10px; text-align: center; }

.show-more-btn {
  background: none;
  border: none;
  color: #003049;
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;
}

.preview-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}

.select-btn {
  background: none;
  border: 1px solid #e9ecef;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  color: #003049;
  cursor: pointer;
  transition: all 0.2s ease;
}

.select-btn:hover { background: #f8f9fa; }

.spin { animation: send-spin 1s linear infinite; }

/* ===== MARKDOWN STYLES ===== */
.markdown-content :deep(h3), .markdown-content :deep(h4) {
  margin: 8px 0 4px;
  font-size: 14px;
  font-weight: 600;
}

.markdown-content :deep(p) { margin-bottom: 8px; }
.markdown-content :deep(ul), .markdown-content :deep(ol) {
  margin-bottom: 8px;
  padding-left: 16px;
}

.markdown-content :deep(li) { margin-bottom: 4px; }

.markdown-content :deep(code) {
  background: #f1f3f4;
  padding: 2px 4px;
  border-radius: 4px;
  font-size: 12px;
}

.markdown-content :deep(pre) {
  background: #2d3748;
  color: #e2e8f0;
  padding: 10px;
  border-radius: 8px;
  overflow-x: auto;
  font-size: 12px;
}

.markdown-content :deep(pre code) { background: none; padding: 0; }

.markdown-content :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 8px 0;
  font-size: 12px;
}

.markdown-content :deep(th), .markdown-content :deep(td) {
  border: 1px solid #e9ecef;
  padding: 6px 8px;
  text-align: left;
}

.markdown-content :deep(th) { background: #f8f9fa; font-weight: 600; }

/* ===== MOBILE ===== */
@media (max-width: 480px) {
  .ai-chatbox { width: calc(100vw - 48px); }
  .create-modal, .save-modal { width: 95%; max-width: none; }
  .target-selectors { grid-template-columns: 1fr; }
}

/* ===== TRANSITIONS ===== */
.fab-icon-enter-active, .fab-icon-leave-active { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
.fab-icon-enter-from { opacity: 0; transform: rotate(-90deg) scale(0.5); }
.fab-icon-leave-to { opacity: 0; transform: rotate(90deg) scale(0.5); }

.chatbox-enter-active { animation: chatbox-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1); }
.chatbox-leave-active { animation: chatbox-out 0.3s ease-in forwards; }

@keyframes chatbox-in {
  0% { opacity: 0; transform: translateY(30px) scale(0.9); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}

@keyframes chatbox-out {
  0% { opacity: 1; transform: translateY(0) scale(1); }
  100% { opacity: 0; transform: translateY(30px) scale(0.9); }
}

.message-enter-active { animation: message-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1); }
.message-leave-active { animation: message-out 0.25s ease-in forwards; }

@keyframes message-in {
  0% { opacity: 0; transform: translateY(20px) scale(0.9); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}

@keyframes message-out {
  0% { opacity: 1; }
  100% { opacity: 0; transform: translateY(-10px) scale(0.9); }
}

.quick-action-enter-active { animation: quick-action-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1); }
.quick-action-leave-active { animation: quick-action-out 0.2s ease-in forwards; }

@keyframes quick-action-in {
  0% { opacity: 0; transform: scale(0.8) translateY(10px); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
}

@keyframes quick-action-out {
  0% { opacity: 1; }
  100% { opacity: 0; transform: scale(0.8); }
}

.modal-enter-active { animation: modal-in 0.3s ease; }
.modal-leave-active { animation: modal-out 0.2s ease forwards; }

@keyframes modal-in {
  0% { opacity: 0; }
  100% { opacity: 1; }
}

@keyframes modal-out {
  0% { opacity: 1; }
  100% { opacity: 0; }
}

.attachment-enter-active { animation: attachment-in 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); }
.attachment-leave-active { animation: attachment-out 0.2s ease-in forwards; }

@keyframes attachment-in {
  from { opacity: 0; transform: scale(0.8) translateY(10px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
}

@keyframes attachment-out {
  from { opacity: 1; }
  to { opacity: 0; transform: scale(0.8) translateX(-10px); }
}

.attachment-chip-enter-active { animation: chip-in 0.3s ease; }
.attachment-chip-leave-active { animation: chip-out 0.2s ease forwards; }

@keyframes chip-in {
  from { opacity: 0; transform: scale(0.8); }
  to { opacity: 1; transform: scale(1); }
}

@keyframes chip-out {
  from { opacity: 1; }
  to { opacity: 0; transform: scale(0.8); }
}

.thinking-enter-active { animation: thinking-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1); }
.thinking-leave-active { animation: thinking-out 0.3s ease-in forwards; }

@keyframes thinking-in {
  0% { opacity: 0; transform: translateX(-20px) scale(0.9); }
  100% { opacity: 1; transform: translateX(0) scale(1); }
}

@keyframes thinking-out {
  0% { opacity: 1; }
  100% { opacity: 0; transform: translateX(-20px) scale(0.9); }
}

.welcome-enter-active { animation: welcome-in 0.5s ease; }
.welcome-leave-active { animation: welcome-out 0.3s ease forwards; }

@keyframes welcome-in {
  from { opacity: 0; transform: scale(0.9); }
  to { opacity: 1; transform: scale(1); }
}

@keyframes welcome-out {
  from { opacity: 1; }
  to { opacity: 0; transform: scale(0.9); }
}

.send-icon-enter-active, .send-icon-leave-active { transition: all 0.2s ease; }
.send-icon-enter-from { opacity: 0; transform: scale(0.5) rotate(-90deg); }
.send-icon-leave-to { opacity: 0; transform: scale(0.5) rotate(90deg); }

@keyframes icon-thinking {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.7; }
}

@keyframes status-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.05); }
}
</style>
